/* eslint-disable eqeqeq */
import $ from "jquery";
import { I18n } from "react-redux-i18n";
import auth, { post, getLocale } from "../auth";
import admin, { getMenuItem, getMenuCategory, cancelOrder } from "../admin";
import { orderMenuItem, setOrderSheetObserver, readOrderSheet } from "../orderMenuItem";
import { getMenuItemAdditions, getMenuCategoryOptions, activeMenuItems, submitNewOrders } from "../tables-overview";
import orderListUtil, { getTableName, getTableOccupation, getTable, deleteOrders, updateTableOccupations } from "../order-list-util";
import { runTableTool, isTableTool, tablesContext, openSelectedTable, showNewOrdersOnScreen, newOrdersOnScreen } from "./voiceTables";
import { showCategoryOnScreen, hideCategoryOnScreen, setMenuTapObserver } from "./menuSelector";

// Voice ordering for waiters. The server only mints a short lived OpenAI Realtime key (aiService/{r}/realtimeOrderSession),
// the audio goes straight from the browser to OpenAI over WebRTC. The model never orders anything itself: it calls the
// tools below, which only change a local draft list. The waiter reviews the list and confirms it, and only then the
// products are ordered through the same orderMenuItem() a tap on a menu item uses.

const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const IDLE_TIMEOUT_MS = 2 * 60 * 1000;
const MAX_SESSION_MS = 10 * 60 * 1000;
const ORDER_GAP_MS = 250;
const MAX_MESSAGES = 30;
// The realtime model has a small context window (about 32k tokens) and the menu shares it with the conversation.
const MENU_CONTEXT_MAX_CHARS = 24000;
// tokens of the conversation (without the instructions) that are kept
const HISTORY_TOKEN_LIMIT = 6000;
// A data channel message must stay small, a longer menu is sent in several parts.
const MENU_CHUNK_CHARS = 12000;
// the API accepts at most 1024 characters for the whole transcription prompt
const TRANSCRIPTION_PROMPT_MAX = 1000;

const MAX_PENDING = 5;

// pending: table actions the waiter still has to confirm on the screen: [{ id, text }]; their code is in pendingRuns
// panelOpen is shared because the button exists twice: in the page header and in the edit table view that covers the header
const state = { status: "idle", activity: "", error: null, lines: [], messages: [], submitting: false, pending: [], notice: null, panelOpen: false, cost: null };
const pendingRuns = new Map();
const listeners = new Set();
let lineSeq = 0;
let pendingSeq = 0;

const snapshot = () => ({ ...state, lines: state.lines.map(l => ({ ...l })), messages: state.messages.slice(), pending: state.pending.map(p => ({ ...p })) });
const emit = () => {
  const s = snapshot();
  listeners.forEach(l => l(s));
};

export const setPanelOpen = open => {
  state.panelOpen = !!open;
  emit();
};

export const subscribe = listener => {
  listeners.add(listener);
  listener(snapshot());
  return () => listeners.delete(listener);
};

// ---------------------------------------------------------------- menu search

const norm = s =>
  String(s == null ? "" : s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokens = s => norm(s).split(" ").filter(Boolean);

const distance = (a, b) => {
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let last = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, last + (a[i - 1] === b[j - 1] ? 0 : 1));
      last = tmp;
    }
  }
  return prev[b.length];
};

// The speech recognition often mishears a letter or two, so close spellings count as a (weaker) match too.
const tokenScore = (q, n) => {
  if (q === n) return 1;
  if (q.length >= 3 && (n.startsWith(q) || (n.length >= 3 && q.startsWith(n)))) return 0.9;
  if (q.length >= 3 && n.includes(q)) return 0.7;
  if (q.length >= 4 && n.length >= 4) {
    const ratio = 1 - distance(q, n) / Math.max(q.length, n.length);
    if (ratio >= 0.7) return ratio * 0.8;
  }
  return 0;
};

const allNames = item => {
  const names = [];
  let name = item.name;
  if (typeof name === "string" && name[0] === "{") {
    try {
      name = JSON.parse(name);
    } catch (e) {
      // plain text
    }
  }
  if (name && typeof name === "object") Object.values(name).forEach(v => typeof v === "string" && names.push(v));
  else if (name) names.push(String(name));
  const local = getLocale(item.name);
  if (local) names.unshift(local);
  if (item.shortName) names.push(item.shortName);
  return names;
};

const sizeLabel = q => String(getLocale(q.label) || "").trim();
const defaultSize = sizes => sizes.find(q => q.def) || sizes[0];

// Ingredients that come with a product (can be left out) or extras that can be added: [{ id, name, quantity }]
const additionList = (full, type) => {
  try {
    const list = [];
    getMenuItemAdditions(full, "", type).forEach(a => {
      const id = Number(a.id);
      if (!list.find(x => x.id === id)) list.push({ id, name: a.text, quantity: Number(String(a.value).split("-")[1]) || 1 });
    });
    return list;
  } catch (e) {
    return [];
  }
};

// An option group (steak doneness, side dish, ...) is a menu category whose products the guest has to / may choose from.
// Many products share the same group, so it is built once.
// A product that lists some products of the category explicitly limits the choices to those (see activeMenuItems),
// so the choices of such a product can differ from the same group of other products.
const limitsChoices = full => {
  let m = full;
  while (m) {
    if ((m.menuItemAdditions || []).some(a => a.additionType === "optional")) return true;
    m = m.menuCategoryId ? getMenuCategory(m.menuCategoryId) : null;
  }
  return false;
};

const groupFor = (full, option, groups) => {
  const type = option.optionType;
  const min = Number(option.min) || 0;
  const max = Number(option.max) || 0;
  const key = [option.additionId, type, min, max].join("|");
  const limited = limitsChoices(full);
  let group = limited ? null : groups.get(key);
  if (!group) {
    const category = getMenuCategory(option.additionId);
    const choices = [];
    activeMenuItems(category, full, true, false).forEach(m => {
      const q = defaultSize((m.availableQuantities || []).filter(s => s.onMenu === true));
      if (q && !choices.find(c => c.id === m.id)) choices.push({ id: m.id, name: getLocale(m.name) || String(m.id), quantity: Number(q.quantity) });
    });
    const limitedKey = limited ? key + "|" + choices.map(c => c.id).join(",") : key;
    group = groups.get(limitedKey);
    if (!group) {
      group = { gid: "G" + (groups.size + 1), additionId: option.additionId, name: (category && getLocale(category.name)) || "", type, min, max, choices };
      groups.set(limitedKey, group);
    }
  }
  return group;
};

let dataSource = null;
let dataCache = { entries: [], tree: [], groups: [], optionalSets: [] };
let groupMap = new Map();
let followCache = new Map();

// Choosing (or adding) a product can bring more option groups with it, for example a menu whose choice of a main course
// requires a side dish and a sauce; the dialog shows them as soon as the product is picked. They can bring ingredients too.
// Returns { groups, included } for the product with the given id, ordered as the base product (entry) sees them.
const followOf = (entry, id) => {
  const key = (limitsChoices(entry.full) ? entry.id + "|" : "") + id;
  let follow = followCache.get(key);
  if (!follow) {
    follow = { groups: [], included: [] };
    followCache.set(key, follow);
    try {
      const chosen = getMenuItem(id, true);
      if (chosen) {
        (getMenuCategoryOptions(chosen) || []).forEach(option => {
          const group = groupFor(entry.full, option, groupMap);
          if (group.choices.length && !follow.groups.includes(group)) follow.groups.push(group);
        });
        follow.included = additionList(chosen, "included");
      }
    } catch (e) {
      console.error("voice order follow-up options failed", id, e);
    }
  }
  return follow;
};

const sameGroup = (a, b) => a === b || (a.additionId === b.additionId && a.type === b.type);

// The option groups that apply to a product when the given products are chosen: its own groups plus, repeatedly,
// the groups that chosen choices and extras bring in. via: gid -> name of the choice that brought the group in.
const activeGroups = (entry, ids) => {
  const active = entry.groups.slice();
  const via = new Map();
  const isChosenHere = id => active.some(g => g.choices.some(c => c.id === id)) || entry.optional.some(x => x.id === id);
  let changed = true;
  while (changed) {
    changed = false;
    ids.forEach(id => {
      if (!isChosenHere(id)) return;
      followOf(entry, id).groups.forEach(g => {
        if (active.some(a => sameGroup(a, g))) return;
        active.push(g);
        via.set(g.gid, menuItemName(id));
        changed = true;
      });
    });
  }
  return { active, via };
};

// the selector on the screen only lists categories that are top and have a position (see CategoryList)
const shownOnScreen = cat => {
  const full = getMenuCategory(cat.id) || cat;
  return !!(full.isTop && full.pos);
};

// what the screen shows (tables overview / POS): active, top and on the POS (defaultPos also needs every parent category on the POS);
// anything else must not be orderable by voice
const onPos = x => !!(x && x.isActive && x.isTop && x.defaultPos);

const menuData = () => {
  const root = admin.categories && admin.categories.activeMenuCategories;
  if (!root) return dataCache;
  if (root === dataSource) return dataCache;
  const found = new Map();
  const groups = new Map();
  const optionalSets = new Map();
  const walk = categories =>
    (categories || []).filter(onPos).map(cat => {
      const category = getLocale(cat.name) || "";
      const node = { id: cat.id, entityType: cat.entityType, shown: shownOnScreen(cat), name: category, entries: [], children: [] };
      (cat.activeMenuItems || []).forEach(i => {
        if (!onPos(i) || found.has(i.id)) return;
        const full = getMenuItem(i.id, true);
        // products without a fixed size (weighed, custom price) need typing on the screen, not speech
        if (!full || full.type === "deleted" || full.available === false || full.freeQuantity) return;
        const sizes = (full.availableQuantities || []).filter(q => q.onMenu === true);
        if (!sizes.length) return;
        const names = allNames(full);
        const included = additionList(full, "included");
        const optional = additionList(full, "optional").filter(o => !included.find(x => x.id === o.id));
        let optionalSet = null;
        if (optional.length) {
          const key = optional.map(o => o.id + "-" + o.quantity).join(",");
          optionalSet = optionalSets.get(key);
          if (!optionalSet) {
            optionalSet = { oid: "O" + (optionalSets.size + 1), items: optional };
            optionalSets.set(key, optionalSet);
          }
        }
        const entryGroups = [];
        try {
          (getMenuCategoryOptions(full) || []).forEach(option => {
            const group = groupFor(full, option, groups);
            if (group.choices.length && !entryGroups.includes(group)) entryGroups.push(group);
          });
        } catch (e) {
          console.error("voice order option groups failed", full.id, e);
        }
        const entry = { id: full.id, full, name: names[0] || String(full.id), nameTokens: names.map(tokens), category, categoryTokens: tokens(category), sizes, included, optional, optionalSet, groups: entryGroups };
        found.set(full.id, entry);
        node.entries.push(entry);
      });
      node.children = walk(cat.activeMenuCategories);
      return node;
    });
  groupMap = groups;
  followCache = new Map();
  const tree = walk(root);
  // the groups that choices and extras bring in (also several levels deep) are known before anything is shown or checked
  found.forEach(entry => {
    // groups the product has anyway (or the group itself) are not "brought in" by a choice, the dialog does not show them twice
    const brought = (follow, group) => follow.groups.filter(g => !entry.groups.some(x => sameGroup(x, g)) && !(group && sameGroup(group, g)));
    entry.optional.forEach(x => (x.follow = brought(followOf(entry, x.id)).map(g => g.gid)));
    const seen = new Set();
    const visit = group => {
      if (seen.has(group.gid)) return;
      seen.add(group.gid);
      group.choices.forEach(c => {
        const follow = followOf(entry, c.id);
        const next = brought(follow, group);
        if (!group.follow) group.follow = new Map();
        if (!group.follow.has(c.id)) group.follow.set(c.id, { groups: next.map(g => g.gid), included: follow.included });
        next.forEach(visit);
      });
    };
    entry.groups.forEach(visit);
  });
  dataSource = root;
  dataCache = { entries: Array.from(found.values()), tree, groups: Array.from(groups.values()), optionalSets: Array.from(optionalSets.values()) };
  return dataCache;
};

const menuIndex = () => menuData().entries;

// ---------------------------------------------------------------- menu context for the model

const sizeText = entry => {
  if (entry.sizes.length === 1 && !sizeLabel(entry.sizes[0])) return "";
  const def = defaultSize(entry.sizes);
  return entry.sizes.map(q => (sizeLabel(q) || String(q.quantity)) + (entry.sizes.length > 1 && q === def ? "*" : "")).join(",");
};

const additionText = a => a.id + ":" + a.name + (a.quantity !== 1 ? " x" + a.quantity : "");

// choices and extras that bring further option groups / ingredients with them
const choiceText = (g, c) => {
  const f = g.follow && g.follow.get(c.id);
  let text = c.id + ":" + c.name;
  if (f && f.groups.length) text += " (brings " + f.groups.join("+") + ")";
  if (f && f.included.length) text += " (incl " + f.included.map(additionText).join("/") + ")";
  return text;
};
const extraText = a => additionText(a) + (a.follow && a.follow.length ? " (brings " + a.follow.join("+") + ")" : "");

// The rules of the option dialog: one = exactly one choice, maxone = at most one (can be skipped),
// separate = a number of choices in total between min and max (the same choice can be taken several times).
const groupRule = g => {
  const min = Number(g.min) || 0;
  const max = Number(g.max) || 0;
  switch (g.type) {
    case "one":
      return "choose exactly 1 (required)";
    case "maxone":
      return "choose at most 1 (optional, may be skipped)";
    case "separate": {
      let range = "any number";
      if (min && max) range = min === max ? "exactly " + min : "at least " + min + " and at most " + max;
      else if (min) range = "at least " + min;
      else if (max) range = "at most " + max;
      return "choose " + range + " in total, the same choice may be taken more than once (" + (min > 0 ? "required" : "optional") + ")";
    }
    default:
      return g.type + (min ? ", at least " + min : "") + (max ? ", at most " + max : "");
  }
};

const groupRequired = g => (g.type === "one" ? 1 : g.type === "separate" ? Number(g.min) || 0 : 0) > 0;

const nodeHasItems = node => node.entries.length > 0 || node.children.some(nodeHasItems);

// level 0: everything; 1: no included ingredients / extras; 2: only names and sizes (add_items still enforces the option rules);
// 3: only the category names, the model has to use search_menu
const renderMenu = level => {
  const data = menuData();
  const lines = ["RESTAURANT MENU. The id of a product is the menu_item_id for the tools. Only products that can be ordered by voice are listed; products without a fixed size are not on this list."];
  if (level < 3) {
    lines.push(
      "Product line: id|name|sizes (* = default size, used when the waiter says no size)" +
        (level < 1 ? "|incl: ingredients that come with it (id:name), can be left out with 'without'|opt: set of extras that can be added (see OPTIONAL SETS)" : "") +
        (level < 2 ? "|grp: option groups to ask about (see OPTION GROUPS)" : "") +
        (level === 2 ? "|+ (the product has ingredients, extras or option groups that are not listed here)" : "") +
        (level < 1 ? ". A product line without incl / opt / grp has none of those: nothing can be left out, added or chosen for it" : "")
    );
  }
  if (level < 2 && data.groups.length) {
    lines.push(
      "",
      "OPTION GROUPS (pass the chosen ids in 'options' of add_items; ask the waiter when a group is required and was not answered). " +
        "A choice marked (brings Gx) makes group Gx apply to the product too as soon as it is chosen, with the rule of Gx (it can be required); " +
        "groups brought in can bring in further groups. (incl ...) = ingredients that come with that choice and can be left out with 'without':"
    );
    data.groups.forEach(g => lines.push(g.gid + ' "' + g.name + '" [' + groupRule(g) + "]: " + g.choices.map(c => choiceText(g, c)).join(", ")));
  }
  if (level < 1 && data.optionalSets.length) {
    lines.push("", "OPTIONAL SETS (extras the waiter may ask for, pass the id in 'options'; (brings Gx) as in OPTION GROUPS):");
    data.optionalSets.forEach(s => lines.push(s.oid + ": " + s.items.map(extraText).join(", ")));
  }
  lines.push("", "MENU");
  const walk = (nodes, path) =>
    nodes.forEach(node => {
      if (!nodeHasItems(node)) return;
      const name = path ? path + " > " + node.name : node.name;
      if (node.entries.length) {
        lines.push("# " + name + (level === 3 ? " (" + node.entries.length + " products)" : ""));
        if (level < 3)
          node.entries.forEach(e => {
            const parts = [e.id, e.name.replace(/\|/g, "/"), sizeText(e)];
            if (level < 1 && e.included.length) parts.push("incl:" + e.included.map(additionText).join(","));
            if (level < 1 && e.optionalSet) parts.push("opt:" + e.optionalSet.oid);
            if (level === 2 && (e.groups.length || e.included.length || e.optionalSet)) parts.push("+");
            if (level < 2 && e.groups.length) parts.push("grp:" + e.groups.map(g => g.gid).join(","));
            lines.push(parts.join("|"));
          });
      }
      walk(node.children, name);
    });
  walk(data.tree, "");
  if (level === 3) lines.push("", "There are too many products to list. Use search_menu for every product; it also returns the options of the product.");
  else if (level === 2) lines.push("", "Ingredients, extras and option groups are not listed. For a product marked + call get_product_options(menu_item_id) (or open_option_sheet) before adding it and before the waiter asks for 'without' or an extra; never guess them. A product without + has none of those.");
  else if (level === 1) lines.push("", "Ingredients that can be left out and extras are not listed. Call get_product_options(menu_item_id) before the waiter asks for 'without' or an extra; never guess them.");
  return lines.join("\n");
};
// The menu is part of what every answer re-reads: by default only names, sizes and a "+" marker are sent, the details come from get_product_options
const MENU_START_LEVEL = 2;

export const buildMenuContext = () => {
  let text = "";
  for (let level = MENU_START_LEVEL; level <= 3; level++) {
    text = renderMenu(level);
    if (text.length <= MENU_CONTEXT_MAX_CHARS) break;
  }
  return text;
};

const chunkText = (text, size) => {
  const parts = [];
  let current = "";
  text.split("\n").forEach(line => {
    if (current && current.length + line.length + 1 > size) {
      parts.push(current);
      current = "";
    }
    current += (current ? "\n" : "") + line.slice(0, size);
  });
  if (current) parts.push(current);
  return parts;
};

// Option groups that choices and extras of a product bring in (also several levels deep): [{ group, by }]
const broughtGroups = entry => {
  const byGid = new Map(menuData().groups.map(g => [g.gid, g]));
  const shown = new Set(entry.groups.map(g => g.gid));
  const result = [];
  const queue = entry.groups.slice();
  const bring = (gids, by) =>
    (gids || []).forEach(gid => {
      const g = byGid.get(gid);
      if (!g || shown.has(gid)) return;
      shown.add(gid);
      result.push({ group: g, by });
      queue.push(g);
    });
  entry.optional.forEach(x => bring(x.follow, x.name));
  while (queue.length) {
    const g = queue.shift();
    g.choices.forEach(c => bring(g.follow && g.follow.get(c.id) && g.follow.get(c.id).groups, c.name));
  }
  return result;
};

const groupJson = (g, by) => ({
  group: g.gid,
  name: g.name,
  type: g.type,
  required: groupRequired(g),
  min: g.type === "one" ? 1 : g.type === "separate" ? Number(g.min) || 0 : 0,
  max: g.type === "one" || g.type === "maxone" ? 1 : Number(g.max) || null,
  rule: groupRule(g),
  applies_only_after_choosing: by || undefined,
  choices: g.choices.map(c => {
    const f = g.follow && g.follow.get(c.id);
    return {
      menu_item_id: c.id,
      name: c.name,
      brings_groups: f && f.groups.length ? f.groups : undefined,
      included_ingredients: f && f.included.length ? f.included.map(a => ({ menu_item_id: a.id, name: a.name })) : undefined
    };
  })
});

// Everything that can be chosen for a product, nothing cut off: the model must not guess.
const productOptions = entry => ({
  menu_item_id: entry.id,
  name: entry.name,
  category: entry.category,
  sizes: entry.sizes.map(sizeLabel).filter(Boolean),
  default_size: sizeLabel(defaultSize(entry.sizes)) || null,
  option_groups: entry.groups.map(g => groupJson(g)),
  groups_brought_in_by_choices: broughtGroups(entry).map(b => groupJson(b.group, b.by)),
  included_ingredients: entry.included.map(a => ({ menu_item_id: a.id, name: a.name })),
  extras: entry.optional.map(a => ({ menu_item_id: a.id, name: a.name, brings_groups: a.follow && a.follow.length ? a.follow : undefined })),
  note:
    "option_groups: choose only from the listed choices and obey the rule. A choice with brings_groups makes those groups apply too as soon as it is chosen (groups_brought_in_by_choices), with their own rule: a required one must be answered as well, and a group brought in is only available together with the choice that brings it. included_ingredients: can be left out via 'without'; extras: can be added via 'options'. Nothing else can be chosen."
});

// short form for the matches after the first: the full options cost a lot of tokens in every later answer
const productBrief = entry => ({
  menu_item_id: entry.id,
  name: entry.name,
  category: entry.category,
  sizes: entry.sizes.map(sizeLabel).filter(Boolean),
  has_options: entry.groups.length > 0 || entry.optional.length > 0 || entry.included.length > 0
});

const searchMenu = (query, limit) => {
  const q = tokens(query);
  if (!q.length) return [];
  const joined = q.join(" ");
  const scored = [];
  menuIndex().forEach(entry => {
    let best = 0;
    entry.nameTokens.forEach(nt => {
      let sum = 0;
      q.forEach(qt => {
        let top = 0;
        nt.forEach(t => (top = Math.max(top, tokenScore(qt, t))));
        entry.categoryTokens.forEach(t => (top = Math.max(top, tokenScore(qt, t) * 0.5)));
        sum += top;
      });
      let score = sum / q.length;
      if (nt.join(" ") === joined) score += 0.5;
      best = Math.max(best, score);
    });
    if (best >= 0.6) scored.push({ entry, score: best });
  });
  scored.sort((a, b) => b.score - a.score || a.entry.name.length - b.entry.name.length);
  return scored.slice(0, Math.max(1, Math.min(limit || 3, 6))).map(({ entry }, i) => (i === 0 ? productOptions(entry) : productBrief(entry)));
};

const getProductOptions = id => {
  const entry = menuIndex().find(e => e.id == id);
  return entry ? productOptions(entry) : { error: "unknown menu_item_id " + id + ", use search_menu first" };
};

// ---------------------------------------------------------------- category choice on the screen

// the category that was opened on the screen so that the waiter picks a product (by voice or by tapping)
let categoryChoice = null;
// the category the assistant opened last; it stays open on the screen after the choice or the session, so it is remembered separately
let voiceCategoryId = null;

const closeCategoryChoice = () => {
  clearTimeout(openCategoryTimer);
  const id = voiceCategoryId;
  categoryChoice = null;
  voiceCategoryId = null;
  if (id != null) hideCategoryOnScreen(id);
};

// every category that has products: { node, path: nodes from the top category down to this one }
const categoriesWithItems = () => {
  const found = [];
  const walk = (nodes, path) =>
    nodes.forEach(node => {
      const here = path.concat(node);
      if (nodeHasItems(node)) found.push({ node, path: here });
      walk(node.children, here);
    });
  walk(menuData().tree, []);
  return found;
};

const findCategories = query => {
  const q = tokens(query);
  if (!q.length) return [];
  const joined = q.join(" ");
  const scored = [];
  categoriesWithItems().forEach(({ node, path }) => {
    const nt = tokens(node.name);
    if (!nt.length) return;
    let sum = 0;
    q.forEach(qt => {
      let top = 0;
      nt.forEach(t => (top = Math.max(top, tokenScore(qt, t))));
      sum += top;
    });
    let score = sum / q.length;
    if (nt.join(" ") === joined) score += 0.5;
    if (score >= 0.7) scored.push({ node, path, score });
  });
  scored.sort((a, b) => b.score - a.score || a.node.name.length - b.node.name.length);
  return scored;
};

const entriesBelow = node => node.entries.concat(...node.children.map(entriesBelow));

const productLine = e => ({ menu_item_id: e.id, name: e.name, sizes: sizeText(e) || undefined });

const MAX_CATEGORY_PRODUCTS = 40;

// the same word with another ending (Hungarian: wrapet, wrapok; menu: Wraps) shares a stem of at least 4 letters
const sameWord = (s, t) => {
  if (tokenScore(s, t) >= 0.9) return true;
  let k = 0;
  while (k < s.length && k < t.length && s[k] === t[k]) k++;
  return k >= 4 && k >= Math.min(s.length, t.length) * 0.8;
};

// what the waiter said last (own words, as transcribed)
let lastHeard = "";
// the utterance for which the model was already corrected, so that it happens once
let correctedFor = "";

// A product whose whole name is in the spoken words and that is more specific than a category name ("Magic Wrap" in the category
// "Wraps"). With only the kind ("a wrap") no product matches, because the product names have more words.
const namedProduct = spoken => {
  if (!spoken.length) return null;
  let best = null;
  menuIndex().forEach(e => {
    e.nameTokens.forEach(nt => {
      if (nt.length < 2 || !nt.every(w => spoken.some(s => sameWord(s, w)))) return;
      if (!best || nt.length > best.words) best = { id: e.id, name: e.name, words: nt.length };
    });
  });
  return best;
};

const showCategoryTool = args => {
  // "Magic Wrap" names a product, the category "Wraps" would only hide it: the waiter said the product, so no choice is needed
  const said = namedProduct(tokens(lastHeard));
  if (said) {
    console.info("voice order show_category skipped:", JSON.stringify(args && args.query), "- the waiter named the product", said.name);
    return { matched: false, note: "The waiter named a specific product: " + said.name + " (menu_item_id " + said.id + "). Do not ask which one. Continue with add_items or open_option_sheet for it." };
  }
  const matches = findCategories(args && args.query);
  if (!matches.length) return { matched: false, note: "No menu category has that name. Use search_menu to look for the product itself." };
  const best = matches[0];
  const { node, path } = best;
  const type = /^drink/i.test(String(path[0].entityType || "")) ? "drink" : "meal";
  const visible = path.every(n => n.shown);
  // an open option sheet stays in front: the category is only listed for the model, not opened behind the sheet
  const shown = visible && !sheet && placeCategory(path.map(n => n.id), type);
  // the selector may need a moment after going into the new orders
  if (visible && !sheet && !shown && getTableInfo().hasTable && !getTableInfo().needsLock) openCategoryWhenPossible(node, path, type);
  console.info("voice order show_category:", args && args.query, "->", path.map(n => n.name).join(" > "), "shown", shown, visible ? "" : "(category hidden in the menu settings)");
  if (!sheet) categoryChoice = { id: node.id, name: node.name };
  if (shown) voiceCategoryId = node.id;
  const subs = node.children.filter(nodeHasItems);
  const others = matches
    .slice(1, 4)
    .filter(m => m.score >= best.score - 0.2)
    .map(m => m.path.map(n => n.name).join(" > "));
  return {
    matched: true,
    category: path.map(n => n.name).join(" > "),
    shown_on_screen: shown,
    products: node.entries.slice(0, MAX_CATEGORY_PRODUCTS).map(productLine),
    subcategories: subs.length
      ? subs.map(s => ({ category: s.name, products: entriesBelow(s).slice(0, 12).map(productLine) }))
      : undefined,
    other_matching_categories: others.length ? others : undefined,
    note: shown
      ? "The category is open on the screen: the waiter can say the product or tap it there. Ask which one (name at most four, all of them if there are fewer, and say that tapping on the screen works too). When the waiter says one, call open_option_sheet for it (the same sheet that opens when it is tapped) unless it is fully specified and has no options; never ask its options by voice without the sheet. A system message tells you when the waiter tapped one instead; then do not ask again and do not add it yourself."
      : "The selector is not visible on the screen (it shows in the ordering mode of a table), so ask which one by voice only. Name at most four, all of them if there are fewer; when the waiter says one, continue with add_items or open_option_sheet."
  };
};

// The model does not always call show_category (it can answer from the menu in its prompt), so the category the waiter names
// is also opened from the spoken text. The selector may not be on the screen yet (the table is still being opened): retry for a while.
let openCategoryTimer = null;

// The menu selector only exists in the new orders of an open table: the screen goes there first, like the waiter would.
const placeCategory = (ids, type) => {
  showNewOrdersOnScreen();
  return showCategoryOnScreen(ids, type);
};

const openCategoryWhenPossible = (node, path, type) => {
  const ids = path.map(n => n.id);
  clearTimeout(openCategoryTimer);
  let tries = 0;
  const attempt = () => {
    if (sheet) return;
    const shown = placeCategory(ids, type);
    console.info("voice order open category:", path.map(n => n.name).join(" > "), "shown", shown, "try", tries + 1);
    if (shown) {
      categoryChoice = { id: node.id, name: node.name };
      voiceCategoryId = node.id;
      return;
    }
    if (++tries < 10) openCategoryTimer = setTimeout(attempt, 600);
  };
  attempt();
};

const openSpokenCategory = (transcript, who = "heard") => {
  if (who === "heard") lastHeard = transcript || "";
  // while a product's option sheet is open, the names spoken are options of that product: the menu behind it stays as it is
  if (sheet) return;
  const spoken = tokens(transcript);
  if (!spoken.length) return;
  // the waiter named a specific product ("Magic Wrap"): it is ordered directly, the category of the kind ("Wraps") stays closed
  if (who === "heard") {
    const product = namedProduct(spoken);
    if (product) {
      console.info("voice order heard:", JSON.stringify(transcript), "-> product", product.name, "(no category opened)");
      return;
    }
  }
  let best = null;
  const names = [];
  categoriesWithItems().forEach(({ node, path }) => {
    const nt = tokens(node.name);
    if (!nt.length) return;
    names.push(node.name + (path.every(n => n.shown) ? "" : " (not shown)"));
    if (!path.every(n => n.shown)) return;
    // what the model says is reliable (it hears the audio): two or more products of one category named in its question identify the category
    if (who === "said") {
      const named = node.entries.filter(e => e.nameTokens.some(t => t.length && t.every(w => spoken.some(s => sameWord(s, w))))).length;
      if (named >= 2 && (!best || !best.products || named > best.products || (named === best.products && node.entries.length < best.node.entries.length))) best = { node, path, products: named, words: 0 };
      return;
    }
    if (!nt.every(t => spoken.some(s => sameWord(s, t)))) return;
    if (!best || nt.length > best.words) best = { node, path, words: nt.length };
  });
  // "already open" only counts while the waiter is still in the new orders; after leaving them the category is gone from the screen
  if (!best || (categoryChoice && categoryChoice.id === best.node.id && newOrdersOnScreen())) {
    console.info("voice order " + who + ":", JSON.stringify(transcript), "-> category", best ? best.node.name + " (already open)" : "none matched among: " + names.join(" | "));
    return;
  }
  const { node, path } = best;
  const type = /^drink/i.test(String(path[0].entityType || "")) ? "drink" : "meal";
  openCategoryWhenPossible(node, path, type);
};

// a real tap on the screen while the waiter is choosing from the opened category
const onMenuTap = tap => {
  const choice = categoryChoice;
  categoryChoice = null;
  // an option dialog that opens because of the tap belongs to the conversation too
  watchTappedSheet(tap);
  if (!choice) return;
  const name = menuItemName(tap.id) || "a product";
  tellModel(
    "The waiter tapped " +
      name +
      " (menu_item_id " +
      tap.id +
      ") on the screen in the open category " +
      choice.name +
      ", so the choice is made. Do not ask again and do not add it yourself: the screen handles it (an option dialog may open that the waiter fills in on the screen)."
  );
};

// What is allowed for a product, appended to the errors so the model can correct itself without guessing.
const allowedText = entry =>
  " Allowed for " +
  entry.name +
  " - ingredients that can be left out ('without'): " +
  (entry.included.map(a => a.id + " " + a.name).join(", ") || "none") +
  "; extras ('options'): " +
  (entry.optional.map(a => a.id + " " + a.name).join(", ") || "none") +
  "; option groups: " +
  (entry.groups.map(g => g.gid + ' "' + g.name + '" [' + groupRule(g) + "]: " + g.choices.map(c => choiceText(g, c)).join(", ")).join(" | ") || "none") +
  broughtGroups(entry)
    .map(b => " | brought in by choosing " + b.by + ": " + b.group.gid + ' "' + b.group.name + '" [' + groupRule(b.group) + "]: " + b.group.choices.map(c => choiceText(b.group, c)).join(", "))
    .join("") +
  ".";

// ---------------------------------------------------------------- draft list

export const getTableInfo = () => {
  const id = sessionStorage.tableOccupationSelected;
  const valid = id && id !== "null" && id !== "undefined" && !!getTableOccupation(id);
  if (valid) return { hasTable: true, tableName: getTableName(sessionStorage.tableNumberSelected) };
  // a table is selected on the screen but nobody locked it yet: it is locked when the list is added
  const number = Number(sessionStorage.tableNumberSelected);
  if (number && getTable(number)) return { hasTable: true, needsLock: true, tableName: getTableName(number) };
  return { hasTable: false, tableName: null };
};

const describe = () => ({
  table: getTableInfo().tableName,
  lines: state.lines.map((l, i) => ({ line: i + 1, menu_item_id: l.menuItemId, name: l.name, count: l.count, size: l.size || undefined, options: l.details || undefined, comment: l.comment || undefined }))
});

// Does the number of chosen options of a group satisfy its rule? Returns the problem as text, or null.
const groupProblem = (entry, group, picked, by) => {
  // the product already includes one of the choices, the dialog does not insist on an answer then
  if (group.choices.some(c => entry.included.some(i => i.id === c.id))) return null;
  const min = group.type === "one" ? 1 : group.type === "separate" ? group.min : 0;
  const max = group.type === "one" || group.type === "maxone" ? 1 : group.max;
  if (picked >= min && (!max || picked <= max)) return null;
  return (
    entry.name +
    ": option group " +
    group.gid +
    ' "' +
    group.name +
    '"' +
    (by ? " (applies because " + by + " was chosen)" : "") +
    " needs " +
    groupRule(group) +
    " but " +
    picked +
    " chosen. Ask the waiter. Choices: " +
    group.choices.map(c => choiceText(group, c)).join(", ")
  );
};

// Turns the options / without lists of the model into the additions of orderMenuItem and a short text for the list.
// Choices and extras can bring further option groups and ingredients with them; those count like the product's own.
const resolveOptions = (entry, options, without, lenient) => {
  const additions = [];
  const labels = [];
  const picked = new Map();
  const list = Array.isArray(options) ? options : [];
  const { active, via } = activeGroups(
    entry,
    list.map(raw => Number(raw && typeof raw === "object" ? raw.menu_item_id : raw))
  );
  const ingredients = entry.included.slice();
  list.forEach(raw => {
    const id = Number(raw && typeof raw === "object" ? raw.menu_item_id : raw);
    if (active.some(g => g.choices.some(c => c.id === id)) || entry.optional.some(x => x.id === id))
      followOf(entry, id).included.forEach(a => !ingredients.some(x => x.id === a.id) && ingredients.push(a));
  });
  for (const raw of list) {
    const o = raw && typeof raw === "object" ? raw : { menu_item_id: raw };
    const id = Number(o.menu_item_id);
    const n = Math.max(1, Math.min(Number(o.count) || 1, 20));
    const group = active.find(g => g.choices.some(c => c.id === id));
    if (group) {
      const choice = group.choices.find(c => c.id === id);
      const separate = group.type === "separate";
      const existing = additions.find(a => a.menuItemId === id);
      if (existing && !separate) continue;
      if (existing) {
        existing.quantity += n;
        picked.set(group.gid, (picked.get(group.gid) || 0) + n);
        continue;
      }
      additions.push({ menuItemId: id, addition: true, quantity: separate ? n : choice.quantity });
      labels.push((separate && n > 1 ? n + "x " : "") + choice.name);
      picked.set(group.gid, (picked.get(group.gid) || 0) + (separate ? n : 1));
      continue;
    }
    const extra = entry.optional.find(x => x.id === id);
    if (extra) {
      if (!additions.find(a => a.menuItemId === id)) {
        additions.push({ menuItemId: id, addition: true, quantity: extra.quantity * n });
        labels.push("+ " + (n > 1 ? n + "x " : "") + extra.name);
      }
      continue;
    }
    return { error: entry.name + ": menu_item_id " + o.menu_item_id + " is not an option or extra of this product (a group brought in by a choice is only available when that choice is chosen too)." + allowedText(entry) };
  }
  for (const raw of Array.isArray(without) ? without : []) {
    const id = Number(raw && typeof raw === "object" ? raw.menu_item_id : raw);
    const ingredient = ingredients.find(x => x.id === id);
    if (!ingredient) return { error: entry.name + ": menu_item_id " + id + " is not an ingredient that can be left out." + allowedText(entry) };
    if (!additions.find(a => a.menuItemId === id)) {
      additions.push({ menuItemId: id, addition: false, quantity: ingredient.quantity });
      labels.push("- " + ingredient.name);
    }
  }
  const missing = [];
  for (const group of active) {
    const problem = groupProblem(entry, group, picked.get(group.gid) || 0, via.get(group.gid));
    if (!problem) continue;
    // the option sheet may be incomplete while the waiter and the model are still working on it
    if (lenient) missing.push(problem);
    else return { error: problem };
  }
  return { additions, details: labels.join(", "), missing };
};

// the size (quantity) of a product for the size the waiter said; no size = the default size
const sizeFor = (entry, size) => {
  if (!size) return { q: defaultSize(entry.sizes) };
  const wanted = norm(size);
  const match = entry.sizes.find(s => norm(sizeLabel(s)) === wanted || String(s.quantity) === wanted) || entry.sizes.find(s => norm(sizeLabel(s)).includes(wanted));
  if (!match) return { error: "unknown size '" + size + "', available: " + entry.sizes.map(s => sizeLabel(s) || s.quantity).join(", ") };
  return { q: match };
};

const addLine = ({ menu_item_id, count, size, comment, options, without }) => {
  const entry = menuIndex().find(e => e.id == menu_item_id);
  if (!entry) return { error: "unknown menu_item_id " + menu_item_id + ", use search_menu first" };
  const chosen = sizeFor(entry, size);
  if (chosen.error) return chosen;
  const q = chosen.q;
  const resolved = resolveOptions(entry, options, without);
  if (resolved.error) return resolved;
  const n = Math.max(1, Math.min(Number(count) || 1, 50));
  const note = (comment || "").trim();
  const key = JSON.stringify(resolved.additions);
  const existing = state.lines.find(l => l.menuItemId == entry.id && l.quantity == q.quantity && (l.comment || "") === note && l.key === key);
  if (existing) existing.count = Math.min(existing.count + n, 99);
  else
    state.lines.push({
      id: ++lineSeq,
      menuItemId: entry.id,
      name: entry.name,
      count: n,
      quantity: q.quantity,
      size: sizeLabel(q),
      comment: note,
      additions: resolved.additions,
      details: resolved.details,
      key
    });
  return null;
};

export const setLineCount = (id, count) => {
  const line = state.lines.find(l => l.id === id);
  if (!line) return;
  if (count < 1) state.lines = state.lines.filter(l => l.id !== id);
  else line.count = Math.min(count, 99);
  emit();
};

export const setLineComment = (id, comment) => {
  const line = state.lines.find(l => l.id === id);
  if (!line) return;
  line.comment = comment;
  emit();
};

export const removeLine = id => {
  state.lines = state.lines.filter(l => l.id !== id);
  emit();
};

export const clearDraft = () => {
  state.lines = [];
  state.messages = [];
  state.error = null;
  emit();
};

// ---------------------------------------------------------------- table actions that need the waiter's confirmation

// The model can only ask for such an action; it happens when the waiter presses the button on the screen.
const confirmAction = (text, run) => {
  if (!state.pending.find(p => p.text === text)) {
    const id = ++pendingSeq;
    state.pending.push({ id, text });
    pendingRuns.set(id, run);
    while (state.pending.length > MAX_PENDING) pendingRuns.delete(state.pending.shift().id);
    state.notice = null;
    // the button is in the panel, so the panel must be open or the waiter has nothing to press
    state.panelOpen = true;
    console.info("voice order waiting for the waiter to confirm:", text);
    emit();
  }
  return { status: "waiting_for_waiter_confirmation", action: text, note: "Not done yet. Tell the waiter to confirm it on the screen. Never say it is done before the waiter confirmed." };
};

const tellModel = text => {
  send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{ type: "input_text", text: text + languageNote() }] } });
};

export const confirmPending = async id => {
  const run = pendingRuns.get(id);
  const item = state.pending.find(p => p.id === id);
  if (!run || !item) return;
  pendingRuns.delete(id);
  state.pending = state.pending.filter(p => p.id !== id);
  state.notice = null;
  emit();
  let result;
  try {
    result = await run();
  } catch (e) {
    console.error("voice table action failed", e);
    result = { error: "failed" };
  }
  state.notice = result.error ? { ok: false, text: result.error } : { ok: true, text: item.text };
  emit();
  tellModel(result.error ? "The waiter confirmed '" + item.text + "' but it failed: " + result.error : "The waiter confirmed and it was done: " + result.text);
};

export const dismissPending = id => {
  const item = state.pending.find(p => p.id === id);
  if (!item) return;
  pendingRuns.delete(id);
  state.pending = state.pending.filter(p => p.id !== id);
  emit();
  tellModel("The waiter dismissed '" + item.text + "'. It was not done.");
};

const clearPending = () => {
  pendingRuns.clear();
  state.pending = [];
  state.notice = null;
};

// ---------------------------------------------------------------- tools called by the model

// Starting an order at a table that is not open yet opens (locks) it first; the products then go on the list of an open table.
// Parallel calls of one answer wait for each other so the table is opened only once.
let openChain = Promise.resolve();

// null when the table is ready, otherwise the error the tool has to report
const prepareTable = async args => {
  if (args.table != null && String(args.table).trim() !== "") {
    const selected = await runTableTool("select_table", { table: args.table, seats: args.seats }, { confirm: confirmAction });
    if (selected.error) return { ...selected, note: "Nothing was added to the list." };
  } else if (getTableInfo().needsLock) {
    const opened = await openSelectedTable();
    if (opened.error) return { error: "the selected table could not be opened: " + opened.error, note: "Nothing was added to the list." };
  }
  // the waiter follows on the screen: the table is open there and the new orders are shown, like after tapping the table and "new order"
  if (getTableInfo().hasTable && !getTableInfo().needsLock) {
    const shown = showNewOrdersOnScreen();
    console.info("voice order table on screen:", getTableInfo().tableName, "new orders shown", shown);
  }
  return null;
};

const addItemsTool = args => {
  const run = async () => {
    try {
      const failed = await prepareTable(args);
      if (failed) return failed;
      const items = Array.isArray(args.items) ? args.items : [];
      // One product that still lacks a required choice: the waiter gets the option sheet, as when tapping the product,
      // instead of an error that the model may turn into a question by voice only.
      if (items.length === 1) {
        const it = items[0] || {};
        const entry = menuIndex().find(e => e.id == it.menu_item_id);
        const chosen = entry && sizeFor(entry, it.size);
        const probe = entry && chosen && !chosen.error && resolveOptions(entry, it.options, it.without, true);
        // the waiter picked it out of the category that is open on the screen: tapping it would open its dialog, so do that when it has options
        const fromCategory = !!categoryChoice && !(it.options && it.options.length) && !(it.without && it.without.length)
          && !!entry && (entry.groups.length || entry.optional.length || entry.included.length);
        console.info("voice order add_items check:", entry ? entry.name : it.menu_item_id, "missing", probe && !probe.error ? probe.missing.length : "n/a", "fromCategory", fromCategory);
        if (probe && !probe.error && (probe.missing.length || fromCategory)) {
          closeCategoryChoice();
          const opened = await openSheetTool({ ...args, menu_item_id: it.menu_item_id, size: it.size, options: it.options, without: it.without });
          console.info("voice order add_items -> option sheet:", entry.name, opened.error ? "failed: " + opened.error : "open");
          if (opened.error) return opened;
          const more = Math.max(1, Number(it.count) || 1) - 1;
          return {
            ...opened,
            added_to_list: false,
            note: "Nothing was put on the list: " + entry.name + (probe.missing.length ? " still needs a required choice, so its" : " was chosen from the open category, so its") + " option sheet was opened on the screen, like when it is tapped. "
              + (probe.missing.length ? "Ask the waiter for the missing parts, one at a time, naming the allowed choices; use update_option_sheet for what is said." : "Ask the waiter whether they want any change (extras or leaving something out), or to press OK; use update_option_sheet for what is said.")
              + (more ? " The waiter wanted " + (more + 1) + " portions: after the sheet is ordered add the other " + more + " with add_items." : "")
              + saidNote(false)
          };
        }
      }
      const errors = [];
      items.forEach(item => {
        const err = addLine(item || {});
        if (err) errors.push(err.error);
      });
      emit();
      // In the ordering state of a table the products are ordered at once, there is nothing to confirm.
      let ordered = null;
      if (state.lines.length && isOrderingState()) {
        ordered = describe().lines.map(l => ({ name: l.name, count: l.count, size: l.size, options: l.options, comment: l.comment }));
        const left = await submitDraft();
        if (left) ordered = null;
      }
      const result = errors.length ? { ...describe(), errors } : describe();
      if (!errors.length) closeCategoryChoice();
      if (ordered) result.ordered = ordered;
      if (!getTableInfo().hasTable) result.note = "No table is selected yet. Ask the waiter which table, then call select_table.";
      return result;
    } catch (e) {
      console.error("voice order add_items failed", e);
      return { error: "tool failed" };
    }
  };
  const result = openChain.then(run);
  openChain = result.catch(() => {});
  return result;
};

// The speech recognition often garbles product names ("Magic Wrap" -> "Magic Frappe"), but the model hears the audio: when it asks
// for the options of a product while the waiter is choosing from the open category (or the product was heard by name), the waiter
// has chosen that product, so its option sheet opens like after a tap instead of relying on the model to open it.
const productOptionsTool = args => {
  const result = getProductOptions(args && args.menu_item_id);
  const entry = menuIndex().find(e => e.id == (args && args.menu_item_id));
  if (!entry || result.error) return result;
  const named = namedProduct(tokens(lastHeard));
  // an open sheet of another product is replaced when the waiter named this one ("instead, a Magic Wrap")
  if (sheet && !(named && named.id == entry.id && sheet.entry.id !== entry.id && wantsSwitch(lastHeard))) return result;
  if (sheet && !readOrderSheet()) abandonSheet();
  const chosen = !!categoryChoice || (!!named && named.id == entry.id);
  const hasOptions = entry.groups.length || entry.optional.length || entry.included.length;
  console.info("voice order get_product_options:", entry.name, "chosen", chosen, "hasOptions", !!hasOptions);
  if (!chosen || !hasOptions) return result;
  return runSheetTool("open_option_sheet", { menu_item_id: entry.id }).then(opened => {
    console.info("voice order get_product_options -> option sheet:", entry.name, opened.error ? "failed: " + opened.error : "open");
    if (opened.error) {
      // usually no table is open yet: the sheet needs it, and the model knows the table the waiter said
      return { ...result, option_sheet_opened: false, note: "The waiter chose " + entry.name + " but its option sheet could not be opened: " + opened.error + " Call open_option_sheet now for menu_item_id " + entry.id + ", with the table the waiter said in the table argument (and seats if said), and with every option the waiter already said. Do not ask the options by voice before the sheet is open." };
    }
    return {
      ...opened,
      options: result,
      note: "The waiter chose " + entry.name + ": its option sheet is open on the screen, like when it is tapped. Do not add it to the list. "
        + (opened.still_missing ? "Ask the waiter for the missing parts, one at a time, naming the allowed choices; use update_option_sheet for what is said." : "Ask the waiter whether they want any change (extras or leaving something out), or to press OK; use update_option_sheet for what is said.")
        + saidNote(false)
    };
  });
};

const runTool = (name, args) => {
  if (isTableTool(name)) return runTableTool(name, args, { confirm: confirmAction });
  if (name === "add_items") return addItemsTool(args);
  if (name === "get_product_options") return productOptionsTool(args);
  if (SELECTED_TOOLS.includes(name)) return runSelectedTool(name, args);
  if (SHEET_TOOLS.includes(name)) return runSheetTool(name, args);
  try {
    switch (name) {
      case "search_menu": {
        const results = searchMenu(args.query, args.limit);
        return results.length
          ? { results, note: results.length > 1 ? "Only the first result lists its options; call get_product_options for another one." : undefined }
          : { results: [], note: "nothing matched" };
      }
      case "get_product_options":
        return getProductOptions(args.menu_item_id);
      case "show_category":
        return showCategoryTool(args);
      case "change_item": {
        const line = state.lines[Number(args.line) - 1];
        if (!line) return { error: "no such line" };
        if (args.count != null) line.count = Math.max(1, Math.min(Number(args.count) || 1, 99));
        if (args.comment != null) line.comment = String(args.comment).trim();
        emit();
        return describe();
      }
      case "remove_item": {
        const line = state.lines[Number(args.line) - 1];
        if (!line) return { error: "no such line" };
        state.lines = state.lines.filter(l => l !== line);
        emit();
        return describe();
      }
      case "get_order":
        return describe();
      default:
        return { error: "unknown tool " + name };
    }
  } catch (e) {
    console.error("voice order tool failed", name, e);
    return { error: "tool failed" };
  }
};

// ---------------------------------------------------------------- realtime session

let pc = null;
let dc = null;
let stream = null;
let audio = null;
let idleTimer = null;
let maxTimer = null;
let handled = new Set();
// A system message alone does not make the model speak. When something on the screen needs an answer (the waiter tapped a product
// whose sheet has open questions) the answer is requested here. A screen action interrupts what the model is saying: the running
// answer is cancelled, the audio that is already buffered on the client is dropped, and the new answer starts once the cancelled
// response is reported done (a response.create before that would be refused).
let responseActive = false;
let respondLater = false;
// what the model said in the running answer, and the utterance for which it was already told that nothing is waiting for the waiter
let lastAssistantText = "";
let confirmCorrectedFor = "";
const CONFIRM_CLAIM = /(v[a\u00e1]r|wait|warte|\u7b49).*(meger[o\u0151]s[i\u00ed]t|visszaigaz|j[o\u00f3]v[a\u00e1]hagy|confirm|best[a\u00e4]tig|\u786e\u8ba4)/i;
const requestAnswer = () => {
  // the model generates faster than it is played, so its voice can still be heard after its response is done
  send({ type: "output_audio_buffer.clear" });
  if (responseActive) {
    respondLater = true;
    send({ type: "response.cancel" });
  } else {
    send({ type: "response.create" });
  }
  state.activity = "thinking";
};

const send = event => {
  if (dc && dc.readyState === "open") dc.send(JSON.stringify(event));
};

// The menu, the tables and the tool results are mostly English or mixed; without a reminder the model drifts into English.
let replyLanguage = "";
const languageNote = () => (replyLanguage ? " (Answer the waiter in " + replyLanguage + ".)" : "");
const withLanguage = result => (replyLanguage && result && typeof result === "object" && !Array.isArray(result) ? { ...result, reply_in_language: replyLanguage } : result);

// Cost: every tool result normally triggers a second answer. The tools that only edit the draft list are confirmed by the model
// in the same answer that calls them (see the instructions), so a clean result needs no further answer. Set to false to compare.
const QUIET_CONFIRMATIONS = true;
const QUIET_TOOLS = ["add_items", "change_item", "remove_item"];
const needsAnswer = (name, result) => {
  if (!QUIET_CONFIRMATIONS || !QUIET_TOOLS.includes(name)) return true;
  // errors, hints (for example no table selected) and already ordered products must be reported
  return !result || typeof result !== "object" || !!(result.error || result.errors || result.note || result.ordered);
};

// The model only knows what we tell it: the menu, its option groups and the extras go into the conversation as system messages.
const sendMenuContext = secret => {
  try {
    // Every answer re-reads the whole conversation; old turns are dropped in bulk once the history grows over the limit.
    // The instructions with the menu are not part of the limit and stay cached.
    send({
      type: "session.update",
      session: { type: "realtime", truncation: { type: "retention_ratio", retention_ratio: 0.5, token_limits: { post_instructions: HISTORY_TOKEN_LIMIT } } }
    });
    // Speech recognition fixed to one language mangles the English product names inside the sentences; the names as a vocabulary hint fix most of it.
    if (secret && secret.transcriptionModel) {
      const names = [];
      const intro =
        "Transcribe exactly what the waiter says" +
        (replyLanguage ? ", in " + replyLanguage : "") +
        ". A restaurant order: quantities, table numbers, product names, also foreign ones as in the menu. Menu: ";
      let length = intro.length;
      menuIndex().forEach(e => {
        if (length + e.name.length + 2 > TRANSCRIPTION_PROMPT_MAX || names.includes(e.name)) return;
        names.push(e.name);
        length += e.name.length + 2;
      });
      if (names.length)
        send({
          type: "session.update",
          session: {
            type: "realtime",
            audio: {
              input: {
                transcription: {
                  model: secret.transcriptionModel,
                  language: secret.transcriptionLanguage,
                  prompt: intro + names.join(", ")
                }
              }
            }
          }
        });
    }
    const tables = tablesContext();
    if (tables) send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{ type: "input_text", text: tables + languageNote() }] } });
    // the menu is already part of the instructions (same for every session, cached); only an older server needs it as messages
    if (secret && secret.menuInInstructions) return;
    const parts = chunkText(buildMenuContext(), MENU_CHUNK_CHARS);
    parts.forEach((text, i) => {
      const label = parts.length > 1 ? "(menu part " + (i + 1) + " of " + parts.length + ")\n" : "";
      send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{ type: "input_text", text: label + text + (i === parts.length - 1 ? languageNote() : "") }] } });
    });
  } catch (e) {
    console.error("voice order menu context failed", e);
  }
};

const touch = () => {
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => stopSession(), IDLE_TIMEOUT_MS);
};

const pushMessage = (role, text) => {
  if (!text || !String(text).trim()) return;
  state.messages.push({ role, text: String(text).trim() });
  if (state.messages.length > MAX_MESSAGES) state.messages.shift();
};

// ---------------------------------------------------------------- cost of a session
// An estimate from the token counts OpenAI reports with every response (USD per 1M tokens). Prices change, check
// the OpenAI pricing page when the number matters; the token counts themselves are exact.
const PRICES = {
  realtime: { textIn: 4, textCached: 0.4, textOut: 16, audioIn: 32, audioCached: 0.4, audioOut: 64 },
  mini: { textIn: 0.6, textCached: 0.06, textOut: 2.4, audioIn: 10, audioCached: 0.3, audioOut: 20 },
  transcribe: { audioIn: 6, textIn: 2.5, textOut: 10, perMinute: 0.006 }
};

let sessionUsage = null;

const newUsage = () => ({ model: "", started: Date.now(), responses: 0, textIn: 0, textCached: 0, audioIn: 0, audioCached: 0, textOut: 0, audioOut: 0, transcribeCost: 0 });

const costSummary = () => {
  const u = sessionUsage;
  if (!u) return null;
  const p = String(u.model).includes("mini") ? PRICES.mini : PRICES.realtime;
  const realtime = ((u.textIn - u.textCached) * p.textIn + u.textCached * p.textCached + (u.audioIn - u.audioCached) * p.audioIn + u.audioCached * p.audioCached + u.textOut * p.textOut + u.audioOut * p.audioOut) / 1e6;
  // the same sum by part, to see what a session costs
  const parts = {
    textUncached: ((u.textIn - u.textCached) * p.textIn) / 1e6,
    textCached: (u.textCached * p.textCached) / 1e6,
    audioIn: ((u.audioIn - u.audioCached) * p.audioIn + u.audioCached * p.audioCached) / 1e6,
    textOut: (u.textOut * p.textOut) / 1e6,
    audioOut: (u.audioOut * p.audioOut) / 1e6
  };
  return {
    parts,
    textOutTokens: u.textOut,
    audioOutTokens: u.audioOut,
    audioInTokens: u.audioIn,
    total: realtime + u.transcribeCost,
    transcription: u.transcribeCost,
    responses: u.responses,
    inputTokens: u.textIn + u.audioIn,
    cachedTokens: u.textCached + u.audioCached,
    outputTokens: u.textOut + u.audioOut,
    seconds: Math.round((Date.now() - u.started) / 1000),
    ended: false
  };
};

const updateCost = () => {
  const cost = costSummary();
  if (cost && cost.responses) state.cost = cost;
};

const addResponseUsage = usage => {
  if (!sessionUsage || !usage) return;
  const d = usage.input_token_details || {};
  const cd = d.cached_tokens_details || {};
  const o = usage.output_token_details || {};
  const hasInputSplit = d.text_tokens != null || d.audio_tokens != null;
  const hasOutputSplit = o.text_tokens != null || o.audio_tokens != null;
  sessionUsage.responses++;
  sessionUsage.textIn += hasInputSplit ? d.text_tokens || 0 : usage.input_tokens || 0;
  sessionUsage.audioIn += hasInputSplit ? d.audio_tokens || 0 : 0;
  // the cached part is a subset of the input; the menu and the instructions are text
  sessionUsage.textCached += cd.text_tokens != null ? cd.text_tokens : cd.audio_tokens != null ? 0 : d.cached_tokens || 0;
  sessionUsage.audioCached += cd.audio_tokens || 0;
  sessionUsage.textOut += hasOutputSplit ? o.text_tokens || 0 : 0;
  sessionUsage.audioOut += hasOutputSplit ? o.audio_tokens || 0 : usage.output_tokens || 0;
  updateCost();
};

// the speech recognition of what the waiter says is billed separately
const addTranscriptionUsage = usage => {
  if (!sessionUsage || !usage) return;
  const p = PRICES.transcribe;
  if (usage.type === "duration") sessionUsage.transcribeCost += ((usage.seconds || 0) / 60) * p.perMinute;
  else {
    const d = usage.input_token_details || {};
    const split = d.audio_tokens != null || d.text_tokens != null;
    sessionUsage.transcribeCost += ((split ? (d.audio_tokens || 0) * p.audioIn + (d.text_tokens || 0) * p.textIn : (usage.input_tokens || 0) * p.audioIn) + (usage.output_tokens || 0) * p.textOut) / 1e6;
  }
  updateCost();
};

const finishCost = () => {
  const cost = costSummary();
  sessionUsage = null;
  if (!cost || !cost.responses) return;
  state.cost = { ...cost, ended: true };
  console.info(
    "voice order session cost: about $" + cost.total.toFixed(4) + " (" + cost.responses + " answers, " + cost.seconds + " s, input " + cost.inputTokens + " tokens of which " + cost.cachedTokens + " cached, output " + cost.outputTokens + " tokens, speech recognition $" + cost.transcription.toFixed(4) + ")"
  );
  console.info(
    "voice order cost parts: uncached text in $" + cost.parts.textUncached.toFixed(4) + ", cached text in $" + cost.parts.textCached.toFixed(4) + ", audio in $" + cost.parts.audioIn.toFixed(4) + " (" + cost.audioInTokens + " tokens), text out $" + cost.parts.textOut.toFixed(4) + " (" + cost.textOutTokens + " tokens), audio out $" + cost.parts.audioOut.toFixed(4) + " (" + cost.audioOutTokens + " tokens), speech recognition $" + cost.transcription.toFixed(4)
  );
};

const handleEvent = e => {
  switch (e.type) {
    case "input_audio_buffer.speech_started":
      state.activity = "listening";
      touch();
      break;
    case "input_audio_buffer.speech_stopped":
      state.activity = "thinking";
      break;
    case "conversation.item.input_audio_transcription.completed":
      pushMessage("waiter", e.transcript);
      addTranscriptionUsage(e.usage);
      try {
        if (cancelSheetByVoice(e.transcript)) break;
        openSpokenCategory(e.transcript);
      } catch (err) {
        console.error("voice order open category failed", err);
      }
      break;
    case "response.output_audio_transcript.delta":
      state.activity = "speaking";
      break;
    case "response.created":
      responseActive = true;
      lastAssistantText = "";
      break;
    case "response.output_audio_transcript.done":
      pushMessage("assistant", e.transcript);
      lastAssistantText = e.transcript || "";
      // the model asks "which wrap?" although the waiter already named the product ("Magic Wrap"): take the question back and tell it to continue with that product
      if (/\?/.test(e.transcript || "") && !sheet) {
        const said = namedProduct(tokens(lastHeard));
        if (said && correctedFor !== lastHeard) {
          correctedFor = lastHeard;
          console.info("voice order assistant asked", JSON.stringify(e.transcript), "although the waiter named", said.name, "- correcting");
          tellModel("The waiter already named the product: " + said.name + " (menu_item_id " + said.id + "). Do not ask which one and do not list products. Continue with that product now: call open_option_sheet for it if it has options to choose, otherwise add_items. Pass everything the waiter already said (size, options, extras, things to leave out) in that call and do not ask for it again. If the waiter said a table and it is not open yet, pass it in the table argument of that call (do not call get_product_options first).");
          requestAnswer();
          break;
        }
      }
      // the speech recognition may misspell what the waiter said, but the model hears the audio: when it asks which product of a category, open that category
      if (!categoryChoice && /\?/.test(e.transcript || "")) {
        try {
          openSpokenCategory(e.transcript, "said");
        } catch (err) {
          console.error("voice order open category failed", err);
        }
      }
      break;
    case "response.done": {
      responseActive = false;
      // cost check: cached_tokens should be most of input_tokens once the session runs
      addResponseUsage(e.response && e.response.usage);
      const usage = e.response && e.response.usage;
      if (usage) {
        const d = usage.input_token_details || {};
        const cd = d.cached_tokens_details || {};
        console.info(
          "voice order tokens: input " + usage.input_tokens + " (text " + (d.text_tokens || 0) + ", audio " + (d.audio_tokens || 0) + "; cached " + (d.cached_tokens || 0) + ": text " + (cd.text_tokens || 0) + ", audio " + (cd.audio_tokens || 0) + "), output " + usage.output_tokens
        );
      }
      const calls = ((e.response && e.response.output) || []).filter(o => o.type === "function_call" && !handled.has(o.call_id));
      if (calls.length) {
        state.activity = "thinking";
        // the table tools talk to the server, so their results arrive later
        Promise.all(
          calls.map(call => {
            handled.add(call.call_id);
            let args = {};
            try {
              args = JSON.parse(call.arguments || "{}");
            } catch (err) {
              args = {};
            }
            console.info("voice order tool call:", call.name, JSON.stringify(args));
            return Promise.resolve(runTool(call.name, args)).then(result => {
              if (result && result.error) console.info("voice order tool error:", call.name, result.error);
              send({ type: "conversation.item.create", item: { type: "function_call_output", call_id: call.call_id, output: JSON.stringify(withLanguage(result)) } });
              return needsAnswer(call.name, result);
            });
          })
        ).then(answers => {
          if (answers.some(Boolean) || respondLater) {
            respondLater = false;
            send({ type: "response.create" });
          }
          emit();
        });
      } else {
        state.activity = "listening";
        // the model says that something waits for the waiter's confirmation, but it called no tool: nothing is on the screen to confirm
        if (!respondLater && !state.pending.length && !sheet && CONFIRM_CLAIM.test(lastAssistantText) && confirmCorrectedFor !== lastHeard) {
          confirmCorrectedFor = lastHeard;
          console.info("voice order assistant said it waits for a confirmation but called no tool:", JSON.stringify(lastAssistantText), "| heard", JSON.stringify(lastHeard));
          tellModel("Nothing is waiting for the waiter: you did not call a tool, so there is nothing to confirm on the screen. Call the matching table tool now (move_table to take or seat a table or its guests over to another table, unlock_table to close a table, add_table to join one more table) with what the waiter said: \"" + lastHeard + "\". Never say that something waits for a confirmation before the tool answered waiting_for_waiter_confirmation.");
          requestAnswer();
          break;
        }
        if (respondLater) {
          respondLater = false;
          send({ type: "response.create" });
        }
      }
      break;
    }
    case "error":
      console.error("realtime error", e.error);
      break;
    default:
      return;
  }
  emit();
};

const fail = code => {
  stopSession();
  state.error = code;
  emit();
};

// The menu goes into the instructions of the session (see RealtimeOrderService): identical for every session of the restaurant,
// so OpenAI prompt caching bills it at the cached price, and it is never dropped by the context window management.
const requestSecret = menu =>
  new Promise((resolve, reject) => {
    const url = "aiService/" + localStorage.restaurantSelected + "/realtimeOrderSession?lang=" + encodeURIComponent(localStorage.language || "en");
    post(url, { menu }, undefined, false, 1, true).done(resolve).fail(reject);
  });

// In the Cordova apps the WebView only gets the microphone when the OS permission was granted first; the speech
// recognition plugin (already part of the app) is the one that asks for it. In a browser the prompt of getUserMedia is enough.
// Only on the native platforms: in a browser (also the Cordova "browser" platform) the plugin has no native side and never answers.
// The timeout makes sure a missing answer can never block the session; getUserMedia then decides.
const ensureNativeMicPermission = () =>
  new Promise(resolve => {
    const platform = window.cordova && window.cordova.platformId;
    const plugin = window.plugins && window.plugins.speechRecognition;
    if ((platform !== "android" && platform !== "ios") || !plugin || !plugin.hasPermission || !plugin.requestPermission) return resolve();
    const timer = setTimeout(resolve, 15000);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const request = () => plugin.requestPermission(done, done);
    try {
      plugin.hasPermission(granted => (granted ? done() : request()), request);
    } catch (e) {
      done();
    }
  });

export const startSession = async () => {
  if (pc || state.status === "connecting") return;
  state.status = "connecting";
  state.activity = "";
  state.cost = null;
  sessionUsage = newUsage();
  state.error = null;
  handled = new Set();
  emit();
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof RTCPeerConnection === "undefined") {
      return fail("mic");
    }
    let secret;
    try {
      let menu = "";
      try {
        menu = buildMenuContext();
      } catch (err) {
        console.error("voice order menu context failed", err);
      }
      secret = await requestSecret(menu);
    } catch (jqXHR) {
      const code = jqXHR && jqXHR.responseJSON && jqXHR.responseJSON.error;
    responseActive = false;
    respondLater = false;
      return fail(code === "voice_order_not_configured" ? "not_configured" : "session_failed");
    }
    replyLanguage = (secret && secret.language) || "";
    if (secret && secret.toolsChars != null)
      console.info("voice order prompt: instructions " + secret.instructionChars + " chars, menu " + secret.menuChars + " chars, tools " + secret.toolsChars + " chars (about 4 chars per token) - every answer reads all of it, model " + secret.model);
    if (sessionUsage) sessionUsage.model = (secret && secret.model) || "";
    // a category left open by the previous session is closed
    closeCategoryChoice();
    lastHeard = "";
    correctedFor = "";
    prefilledFor = "";
    setMenuTapObserver(onMenuTap);
    try {
      await ensureNativeMicPermission();
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (err) {
      return fail("mic");
    }
    if (state.status !== "connecting") {
      // stopped while waiting for the key or the microphone permission
      stream.getTracks().forEach(t => t.stop());
      stream = null;
      return;
    }
    pc = new RTCPeerConnection();
    audio = document.createElement("audio");
    audio.autoplay = true;
    pc.ontrack = ev => {
      audio.srcObject = ev.streams[0];
    };
    pc.onconnectionstatechange = () => {
      if (pc && (pc.connectionState === "failed" || pc.connectionState === "disconnected" || pc.connectionState === "closed") && state.status !== "idle") fail("connection");
    };
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    dc = pc.createDataChannel("oai-events");
    dc.onmessage = ev => {
      try {
        handleEvent(JSON.parse(ev.data));
      } catch (err) {
        console.error("realtime event failed", err);
      }
    };
    dc.onopen = () => {
      sendMenuContext(secret);
      startOrderContextSync();
      state.status = "active";
      state.activity = "listening";
      emit();
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const response = await fetch(REALTIME_CALLS_URL, {
      method: "POST",
      body: offer.sdp,
      headers: { Authorization: "Bearer " + secret.clientSecret, "Content-Type": "application/sdp" }
    });
    if (!response.ok) throw new Error("realtime answer " + response.status);
    const sdp = await response.text();
    if (!pc) return; // stopped meanwhile
    await pc.setRemoteDescription({ type: "answer", sdp });
    touch();
    clearTimeout(maxTimer);
    maxTimer = setTimeout(() => stopSession(), MAX_SESSION_MS);
  } catch (err) {
    console.error("voice order start failed", err);
    fail("connection");
  }
};

export const stopSession = () => {
  clearTimeout(idleTimer)
  stopOrderContextSync();;
  clearTimeout(maxTimer);
  const connection = pc;
  pc = null;
  if (dc) {
    dc.onmessage = null;
    dc.onopen = null;
    try {
      dc.close();
    } catch (e) {
      // already closed
    }
    dc = null;
  }
  if (connection) {
    connection.onconnectionstatechange = null;
    try {
      connection.close();
    } catch (e) {
      // already closed
    }
  }
  if (stream) {
    stream.getTracks().forEach(t => t.stop());
    stream = null;
  }
  if (audio) {
    audio.srcObject = null;
    audio = null;
  }
  finishCost();
  setMenuTapObserver(null);
  categoryChoice = null;
  clearPending();
  abandonSheet(); // an open sheet stays on the screen as a normal dialog for the waiter
  state.status = "idle";
  state.activity = "";
  emit();
};

// ---------------------------------------------------------------- confirm: order the draft

const orderOne = line =>
  new Promise((resolve, reject) => {
    // a detached element behaves like the tapped menu item (table lock, option dialog), it has no date of its own
    const item = document.createElement("li");
    const menuItem = getMenuItem(line.menuItemId, true);
    // add_items checked the required option groups, so the option dialog is not needed
    const additions = (line.additions || []).map(a => ({ ...a }));
    additions.voiceComplete = true;
    const result = orderMenuItem(item, menuItem.entityType, line.menuItemId, line.quantity, undefined, undefined, undefined, line.comment || undefined, additions, undefined, undefined, undefined, undefined, undefined, undefined, undefined);
    $.when(result).done(() => resolve()).fail(() => reject());
  });

const pause = ms => new Promise(resolve => setTimeout(resolve, ms));

// Returns the number of products that could not be ordered; those stay on the list.
export const submitDraft = async () => {
  if (state.submitting || !state.lines.length) return 0;
  if (!getTableInfo().hasTable) {
    state.error = "no_table";
    emit();
    return state.lines.length;
  }
  state.submitting = true;
  state.error = null;
  emit();
  if (getTableInfo().needsLock) {
    const opened = await openSelectedTable();
    if (opened.error || !getTableInfo().hasTable || getTableInfo().needsLock) {
      state.submitting = false;
      state.error = "lock_failed";
      emit();
      return state.lines.length;
    }
  }
  const remaining = [];
  let blocked = false;
  // Outside the ordering state of a table (the "new order" view) the products are ordered like "Order and print":
  // they are created as new (selected) orders and then submitted, accepted and printed. At a point of sale table
  // the rest of the app handles it, so it stays as it was.
  const quick = isQuickOrderTable();
  const previousStates = orderListUtil.statesShown;
  if (quick) orderListUtil.statesShown = newOrderStates();
  for (const line of state.lines.slice()) {
    let done = 0;
    while (!blocked && done < line.count) {
      try {
        await orderOne(line);
        done++;
        await pause(ORDER_GAP_MS);
      } catch (e) {
        // the waiter closed the option dialog: keep this and the rest of the list
        blocked = true;
      }
    }
    if (done < line.count) remaining.push({ ...line, count: line.count - done });
  }
  if (quick) {
    try {
      await orderAndPrint();
    } catch (e) {
      console.error("voice order: order and print failed", e);
      state.error = "print_failed";
    } finally {
      // submitNewOrders restores the states itself; this covers the cases where it did not run
      if (orderListUtil.statesShown !== orderListUtil.statesShownOriginal) orderListUtil.statesShown = previousStates;
    }
  }
  state.lines = remaining;
  state.submitting = false;
  if (remaining.length) state.error = "order_incomplete";
  emit();
  return remaining.reduce((sum, l) => sum + l.count, 0);
};

// ---------------------------------------------------------------- order and print outside the ordering state

const isOrderingState = () => localStorage.selectNewOrders === "true" && getTableInfo().hasTable && !getTableInfo().needsLock;

const isQuickOrderTable = () => {
  const table = getTable(sessionStorage.tableNumberSelected);
  if (!table || table.pos || orderListUtil.kioskMode) return false;
  return localStorage.selectNewOrders !== "true";
};

const newOrderStates = () => {
  const settings = auth.myStatus && auth.myStatus.restaurant_settings && auth.myStatus.restaurant_settings["tables-overview"];
  const states = settings && settings.newOrdersStatesShown;
  return Array.isArray(states) && states.length && states.find(s => s !== "cancelled" && s !== "ordered") === "selected" ? states : ["selected"];
};

// orderMenuItem posts the new orders in the background (temporary negative ids until the server answered)
const waitForPostedOrders = async () => {
  if (auth.delayedOrder && auth.delayedOrder()) return;
  const pending = () =>
    (orderListUtil.ordersCache || []).some(o => o.state === "selected" && o.id < 0 && o.tableOccupationId == sessionStorage.tableOccupationSelected && o.tableNumber == sessionStorage.tableNumberSelected);
  const until = Date.now() + 10000;
  while (pending() && Date.now() < until) await pause(200);
};

const orderAndPrint = async () => {
  await waitForPostedOrders();
  await new Promise((resolve, reject) => {
    const result = submitNewOrders(true, true);
    // submitNewOrders never answers when something was left unsent, so do not wait forever
    const timer = setTimeout(reject, 20000, new Error("submit timeout"));
    $.when(result)
      .done(() => {
        clearTimeout(timer);
        resolve();
      })
      .fail(() => {
        clearTimeout(timer);
        reject(new Error("submit failed"));
      });
  });
};

// ---------------------------------------------------------------- the new orders of the ordering state

// In the ordering state of a table the products the waiter picked are "selected" orders: on the table, but not sent yet.
// The model always gets the current list (a system message that is replaced whenever the list changes) and can repeat,
// change or remove those orders. Orders that were already sent are not part of it.
const SELECTED_TOOLS = ["get_selected_orders", "repeat_selected_orders", "change_selected_orders", "remove_selected_orders"];
const SELECTED_CONTEXT_INTERVAL_MS = 1500;
const SELECTED_CONTEXT_MAX_ROWS = 40;
const MAX_REPEAT_ORDERS = 50;

let orderContextTimer = null;
let orderContextText = null;
let orderContextItem = null;
let orderContextSeq = 0;

const validChildren = o => (o.childOrders || []).filter(c => c && c.menuItem && c.recordState !== "DELETED");

const menuItemName = id => {
  try {
    const m = getMenuItem(id, true);
    return (m && getLocale(m.name)) || String(id);
  } catch (e) {
    return String(id);
  }
};

// oldest first: the ids of the server grow, orders that are still being sent have temporary negative ids and are the newest
const selectedOrders = () => {
  const occupation = sessionStorage.tableOccupationSelected;
  const tableNumber = Number(sessionStorage.tableNumberSelected);
  const rank = o => (o.id > 0 ? o.id : 1e12 - o.id);
  return (orderListUtil.ordersCache || [])
    .filter(o => o.state === "selected" && !o.parentOrderId && o.menuItem && o.recordState !== "DELETED" && !o.paid && o.tableOccupationId == occupation && (!tableNumber || o.tableNumber == tableNumber))
    .sort((a, b) => rank(a) - rank(b));
};

// identical orders are shown as one row
const selectedRows = () => {
  const rows = [];
  selectedOrders().forEach(o => {
    const children = validChildren(o).map(c => [Number(c.menuItem.id), !!c.addition, Number(c.quantity)]).sort((a, b) => a[0] - b[0]);
    const key = JSON.stringify([o.menuItem.id, o.quantity, o.comment || "", o.forWhomId, children]);
    // only neighbours are merged, so the order of the additions stays visible
    const existing = rows.length && rows[rows.length - 1].key === key ? rows[rows.length - 1] : null;
    if (existing) {
      existing.order_ids.push(Number(o.id));
      return;
    }
    let full = null;
    try {
      full = getMenuItem(o.menuItem.id, true);
    } catch (e) {
      // unknown product: shown without a size
    }
    const size = full && (full.availableQuantities || []).find(q => q.quantity == o.quantity);
    rows.push({
      key,
      order_ids: [Number(o.id)],
      menu_item_id: Number(o.menuItem.id),
      name: menuItemName(o.menuItem.id),
      size: (size && sizeLabel(size)) || undefined,
      options: validChildren(o).map(c => ({ menu_item_id: Number(c.menuItem.id), name: menuItemName(c.menuItem.id), kind: c.addition ? "added" : "removed" })),
      comment: o.comment || undefined
    });
  });
  return rows.map(r => ({ ...r, key: undefined, count: r.order_ids.length }));
};

const selectedOrdersContext = () => {
  if (!isOrderingState()) return "";
  const rows = selectedRows();
  const lines = [
    "ORDERING MODE, table " + getTableInfo().tableName + ". Current list of the NEW orders on this table (not sent yet), oldest first, the last row is the newest. Orders sent earlier are not listed and cannot be changed. " +
      "A fresh message like this follows whenever the list changes; only the latest one is valid, ignore older ones. Use the order_ids of this list for repeat_selected_orders, change_selected_orders and remove_selected_orders."
  ];
  if (!rows.length) lines.push("(the list is empty)");
  rows.slice(-SELECTED_CONTEXT_MAX_ROWS).forEach(r => {
    const parts = ["order_ids " + r.order_ids.join(","), r.count + "x " + r.name + " (menu_item_id " + r.menu_item_id + ")"];
    if (r.size) parts.push("size " + r.size);
    if (r.options.length) parts.push(r.options.map(o => (o.kind === "added" ? "+" : "-") + o.name + " (" + o.menu_item_id + ")").join(", "));
    if (r.comment) parts.push("note: " + r.comment);
    lines.push("- " + parts.join(" | "));
  });
  return lines.join("\n");
};

const syncOrderContext = force => {
  if (!dc || dc.readyState !== "open") return;
  let text = "";
  try {
    text = selectedOrdersContext();
  } catch (e) {
    console.error("voice order: selected orders context failed", e);
    return;
  }
  if (text === orderContextText && !force) return;
  // Never delete or replace an earlier item: that would invalidate the prompt cache for everything after it (the audio of
  // the conversation is billed about 80 times higher when it is not cached). A new message is appended instead.
  const hadText = !!orderContextText;
  orderContextText = text;
  if (!text && !hadText) return;
  const id = "sel_orders_" + ++orderContextSeq;
  const body = text || "ORDERING MODE ended: the table is not in ordering mode any more. Ignore every earlier ORDERING MODE list.";
  send({ type: "conversation.item.create", item: { id, type: "message", role: "system", content: [{ type: "input_text", text: body + languageNote() }] } });
  orderContextItem = text ? id : null;
};

const startOrderContextSync = () => {
  clearInterval(orderContextTimer);
  orderContextText = null;
  orderContextItem = null;
  syncOrderContext(true);
  orderContextTimer = setInterval(() => syncOrderContext(false), SELECTED_CONTEXT_INTERVAL_MS);
};

const stopOrderContextSync = () => {
  clearInterval(orderContextTimer);
  orderContextTimer = null;
  orderContextText = null;
  orderContextItem = null;
};

const selectedResult = () => ({ table: getTableInfo().tableName, selected_orders: selectedRows() });

// the options of an existing order in the form of add_items: { options: [{ menu_item_id, count }], without: [id] }
const optionInputs = (entry, order) => {
  const options = [];
  const without = [];
  validChildren(order).forEach(c => {
    const id = Number(c.menuItem.id);
    const quantity = Number(c.quantity) || 1;
    if (!c.addition) {
      without.push(id);
      return;
    }
    const extra = entry.optional.find(x => x.id === id);
    options.push({ menu_item_id: id, count: extra ? Math.max(1, Math.round(quantity / extra.quantity)) : quantity });
  });
  return { options, without };
};

const optionId = raw => Number(raw && typeof raw === "object" ? raw.menu_item_id : raw);

const changedInputs = (entry, current, args) => {
  // the model often takes the id of the stand-alone product (search_menu) instead of the choice of the option group with the same name
  const groupChoices = [];
  sheetGroups(entry).forEach(g => g.choices.forEach(c => groupChoices.push(c)));
  const validIds = new Set(groupChoices.map(c => c.id).concat(entry.optional.map(x => x.id), entry.included.map(x => x.id)));
  const fixId = id => {
    if (validIds.has(id)) return id;
    const wanted = norm(menuItemName(id));
    const same = groupChoices.filter(c => norm(c.name) === wanted);
    if (same.length === 1) {
      console.info("voice order option id", id, "is not a choice of", entry.name + ", taking the choice of the same name:", same[0].id, same[0].name);
      return same[0].id;
    }
    return id;
  };
  const fixOption = raw => (raw && typeof raw === "object" ? { ...raw, menu_item_id: fixId(Number(raw.menu_item_id)) } : fixId(Number(raw)));
  args = { ...args, options: (Array.isArray(args.options) ? args.options : []).map(fixOption), remove_options: (Array.isArray(args.remove_options) ? args.remove_options : []).map(fixOption) };
  // an option passed with count 0 means "take it away" (the model does that), it must not be added once
  const zero = o => !!o && typeof o === "object" && o.count != null && Number(o.count) === 0;
  const given = args.options;
  const removed = new Set(args.remove_options.concat(given.filter(zero)).map(optionId));
  // "change the onion rings to large fries": the choice that the waiter named and that the new one replaces is taken away even when
  // the model only passed the new one, whatever the rule of the group says
  const fresh = given.filter(raw => !zero(raw)).map(optionId);
  const spoken = tokens(lastHeard);
  if (spoken.length && fresh.length) {
    const { said, fits } = spokenMatcher(entry, spoken);
    sheetGroups(entry).forEach(group => {
      if (!group.choices.some(c => fresh.includes(c.id))) return;
      current.options.forEach(o => {
        const c = group.choices.find(x => x.id === o.menu_item_id);
        if (c && !fresh.includes(c.id) && fits(said(c.name))) {
          console.info("voice order swapped out what the waiter named:", c.name);
          removed.add(c.id);
        }
      });
    });
  }
  let options = current.options.filter(o => !removed.has(o.menu_item_id));
  let without = current.without.filter(id => !removed.has(id));
  given.filter(raw => !zero(raw)).forEach(raw => {
    const o = raw && typeof raw === "object" ? raw : { menu_item_id: raw };
    const id = Number(o.menu_item_id);
    const { active } = activeGroups(
      entry,
      options.map(x => x.menu_item_id).concat(id)
    );
    const group = active.find(g => g.choices.some(c => c.id === id));
    // a new choice in a group that allows only one replaces the old choice ("change the medium fries to large")
    if (group && (group.type === "one" || group.type === "maxone" || Number(group.max) === 1)) options = options.filter(x => !group.choices.some(c => c.id === x.menu_item_id));
    options = options.filter(x => x.menu_item_id !== id);
    options.push({ menu_item_id: id, count: o.count });
  });
  // choices that were only possible because of a choice that is gone are dropped too
  const knownChoice = id => menuData().groups.some(g => g.choices.some(c => c.id === id));
  for (let again = true; again; ) {
    again = false;
    const { active } = activeGroups(
      entry,
      options.map(x => x.menu_item_id)
    );
    const kept = options.filter(x => !knownChoice(x.menu_item_id) || active.some(g => g.choices.some(c => c.id === x.menu_item_id)) || entry.optional.some(e => e.id === x.menu_item_id));
    if (kept.length !== options.length) {
      options = kept;
      again = true;
    }
  }
  // ... and so are left out ingredients of choices that are gone
  const ownIngredient = id => entry.included.some(a => a.id === id);
  const keptIds = options.map(x => x.menu_item_id);
  const stillThere = id =>
    keptIds.some(k => {
      const f = followOf(entry, k);
      return f.included.some(a => a.id === id);
    });
  const goneIngredient = id =>
    !ownIngredient(id) &&
    !stillThere(id) &&
    menuData().groups.some(g => g.follow && Array.from(g.follow.values()).some(f => f.included.some(a => a.id === id)));
  without = without.filter(id => !goneIngredient(id));
  (Array.isArray(args.without) ? args.without : []).forEach(raw => {
    const id = optionId(raw);
    if (!without.includes(id)) without.push(id);
  });
  return { options, without };
};

// creates a new selected order like a tap on the menu item does (copy: no option dialog)
const createSelected = (order, { quantity, comment, additions }) =>
  new Promise((resolve, reject) => {
    const full = getMenuItem(order.menuItem.id, true);
    const adds = additions.map(a => ({ ...a }));
    adds.voiceComplete = true;
    const timer = setTimeout(() => reject(new Error("timeout")), 15000);
    const result = orderMenuItem(null, full.entityType, order.menuItem.id, quantity, true, order.forWhomId, order.timing, comment || undefined, adds, undefined, undefined, undefined, order.takeaway, false, true);
    $.when(result)
      .done(() => {
        clearTimeout(timer);
        resolve();
      })
      .fail(() => {
        clearTimeout(timer);
        reject(new Error("failed"));
      });
  });

const currentAdditions = order => validChildren(order).map(c => ({ menuItemId: Number(c.menuItem.id), addition: !!c.addition, quantity: Number(c.quantity) }));

const cancelSelected = id =>
  new Promise(resolve => {
    if (Number(id) < 0) {
      deleteOrders([id]);
      return resolve(true);
    }
    const timer = setTimeout(() => resolve(false), 15000);
    cancelOrder(
      id,
      orderListUtil.statesShown,
      data => {
        clearTimeout(timer);
        if (data == null) return resolve(false);
        deleteOrders(data.deletedOrders, true);
        updateTableOccupations([data.tableOccupation], data.orders);
        resolve(true);
      },
      () => {
        clearTimeout(timer);
        resolve(false);
      }
    );
  });

const removeSelected = async orders => {
  let removed = 0;
  for (const o of orders) {
    if (await cancelSelected(o.id)) removed++;
  }
  return removed === orders.length ? { removed } : { error: "only " + removed + " of " + orders.length + " orders could be removed" };
};

// Deleting never happens on the model's word alone: the waiter presses the button on the screen.
const confirmRemoveSelected = orders => {
  const items = orders.map(o => (Number(o.quantity) > 1 ? o.quantity + " x " : "") + menuItemName(o.menuItem.id)).join(", ");
  const text = I18n.t("admin_local.voice_remove_orders_confirm", { defaultValue: "Delete the order: %{items}?", items });
  const ids = orders.map(o => Number(o.id));
  const run = () => {
    const result = openChain.then(async () => {
      if (!isOrderingState()) return { error: "Not in ordering mode of a table" };
      await waitForPostedOrders();
      const current = ids.map(id => selectedOrders().find(o => Number(o.id) === id)).filter(Boolean);
      if (!current.length) return { error: "the orders are not on the list any more" };
      const outcome = await removeSelected(current);
      await waitForPostedOrders();
      syncOrderContext(false);
      return outcome.error ? outcome : { text: "removed " + items };
    });
    openChain = result.catch(() => {});
    return result;
  };
  return confirmAction(text, run);
};

const repeatSelected = async (orders, args) => {
  const times = Math.max(1, Math.min(Number(args.times) || 1, 10));
  if (orders.length * times > MAX_REPEAT_ORDERS) return { error: "too many orders at once, the maximum is " + MAX_REPEAT_ORDERS };
  let created = 0;
  try {
    for (const o of orders) {
      for (let i = 0; i < times; i++) {
        await createSelected(o, { quantity: o.quantity, comment: o.comment, additions: currentAdditions(o) });
        created++;
        await pause(ORDER_GAP_MS);
      }
    }
  } catch (e) {
    return { error: "only " + created + " of " + orders.length * times + " orders could be created" };
  }
  return { created };
};

// An existing order is changed by creating the changed order first and then removing the old one,
// so nothing is lost when something fails. Everything is checked before anything is touched.
const changeSelected = async (orders, args) => {
  const plan = [];
  for (const o of orders) {
    const entry = menuIndex().find(e => e.id == o.menuItem.id);
    if (!entry) return { error: menuItemName(o.menuItem.id) + " cannot be changed by voice, the waiter has to edit it on the screen" };
    let size = entry.sizes.find(s => s.quantity == o.quantity) || { quantity: o.quantity };
    if (args.size) {
      const wanted = norm(args.size);
      const match = entry.sizes.find(s => norm(sizeLabel(s)) === wanted || String(s.quantity) === wanted) || entry.sizes.find(s => norm(sizeLabel(s)).includes(wanted));
      if (!match) return { error: "unknown size '" + args.size + "', available: " + entry.sizes.map(s => sizeLabel(s) || s.quantity).join(", ") };
      size = match;
    }
    // the model picked a choice that the waiter did not say although the waiter named another one of the same group
    const wrong = wrongPick(entry, (Array.isArray(args.options) ? args.options : []).map(optionId));
    if (wrong) {
      console.info("voice order change_selected_orders refused, not what the waiter said:", wrong, "| heard", JSON.stringify(lastHeard));
      return { error: "That is not what the waiter said (" + wrong + "). Choose again by the name the waiter said, or ask the waiter. Nothing was changed." };
    }
    const inputs = changedInputs(entry, optionInputs(entry, o), args);
    const resolved = resolveOptions(entry, inputs.options, inputs.without);
    if (resolved.error) return { error: resolved.error };
    const comment = args.comment != null ? String(args.comment).trim() : o.comment || "";
    plan.push({ order: o, quantity: Number(size.quantity), comment, additions: resolved.additions });
  }
  let changed = 0;
  try {
    for (const p of plan) {
      await createSelected(p.order, p);
      await pause(ORDER_GAP_MS);
    }
  } catch (e) {
    return { error: "the changed orders could not be created, the old orders are untouched; check the list for duplicates" };
  }
  for (const p of plan) {
    if (await cancelSelected(p.order.id)) changed++;
  }
  return changed === plan.length ? { changed } : { error: "the changed orders were created but only " + changed + " of " + plan.length + " old orders could be removed; check the list for duplicates" };
};

const selectedToolRun = async (name, args) => {
  try {
    if (!isOrderingState()) return { error: "Not in ordering mode of a table. These tools only work on the new orders of an open table in ordering mode." };
    await waitForPostedOrders();
    if (name === "get_selected_orders") return selectedResult();
    const ids = Array.from(new Set((Array.isArray(args.order_ids) ? args.order_ids : [args.order_ids]).map(Number).filter(Boolean)));
    const all = selectedOrders();
    const picked = ids.map(id => all.find(o => Number(o.id) === id));
    if (!ids.length || picked.some(o => !o)) return { error: "unknown order_ids, use the order_ids of the current list", ...selectedResult() };
    if (name === "remove_selected_orders") return confirmRemoveSelected(picked);
    let outcome;
    if (name === "repeat_selected_orders") outcome = await repeatSelected(picked, args);
    else outcome = await changeSelected(picked, args);
    await waitForPostedOrders();
    syncOrderContext(false);
    return { ...outcome, ...selectedResult() };
  } catch (e) {
    console.error("voice order selected orders tool failed", name, e);
    return { error: "tool failed" };
  }
};

// serialized with add_items: both change the orders of the table
const runSelectedTool = (name, args) => {
  const result = openChain.then(() => selectedToolRun(name, args));
  openChain = result.catch(() => {});
  return result;
};

// ---------------------------------------------------------------- option sheet shared with the waiter
// The real option dialog of one product is opened on the screen, filled in by the model. The waiter can tap in it
// meanwhile, the model is told about the taps, and confirm / close work from both sides.

const SHEET_TOOLS = ["open_option_sheet", "update_option_sheet", "confirm_option_sheet", "close_option_sheet"];
const SHEET_ECHO_MS = 700;

let sheet = null; // { entry, item, quick, timer, lastKey, silent }
let sheetFinish = Promise.resolve({});

const sheetStrings = additions => additions.map(a => a.menuItemId + "-" + a.quantity + "-" + a.addition);

// default ingredients of the product, including those that chosen options bring along: id -> quantity
const ingredientQuantities = entry => {
  const map = new Map(entry.included.map(a => [a.id, a.quantity]));
  const add = list => (list || []).forEach(a => { if (!map.has(a.id)) map.set(a.id, a.quantity); });
  const all = menuData().groups || [];
  const seen = new Set();
  const visit = group => {
    if (!group || seen.has(group.gid)) return;
    seen.add(group.gid);
    if (!group.follow) return;
    group.follow.forEach(f => {
      add(f.included);
      f.groups.forEach(gid => visit(all.find(x => x.gid === gid)));
    });
  };
  entry.groups.forEach(visit);
  entry.optional.forEach(x => add(followOf(entry, x.id).included));
  return map;
};

// the "id-quantity-flag" strings of the dialog -> options / without as the tools use them
const sheetInputs = (entry, adds) => {
  const choices = new Set();
  (menuData().groups || []).forEach(g => (g.choices || []).forEach(c => choices.add(c.id)));
  const ingredients = ingredientQuantities(entry);
  const options = [];
  const without = [];
  (adds || []).forEach(text => {
    const [idText, qText, flag] = String(text).split("-");
    const id = Number(idText);
    const q = Number(qText) || 1;
    const extra = entry.optional.find(x => x.id === id);
    if (ingredients.has(id) && !extra && !choices.has(id)) {
      // "-false" = removed ingredient; "-true" with the default quantity = ingredient left out inside an option category
      if ((flag === "false" || ingredients.get(id) === q) && !without.includes(id)) without.push(id);
      return;
    }
    if (flag !== "true") return;
    if (extra) options.push({ menu_item_id: id, count: Math.max(1, Math.round(q / (extra.quantity || 1))) });
    else if (choices.has(id)) options.push({ menu_item_id: id, count: q });
  });
  return { options, without };
};

const sheetState = () => {
  if (!sheet) return null;
  const read = readOrderSheet();
  if (!read) return null;
  const inputs = sheetInputs(sheet.entry, read.adds);
  const resolved = resolveOptions(sheet.entry, inputs.options, inputs.without, true);
  const missing = resolved.missing || [];
  return { read, resolved, key: JSON.stringify([String(read.quantity), resolved.error || resolved.details, missing.length]) };
};

const sheetResult = (s, extra) => {
  if (s.resolved.error) return { error: s.resolved.error };
  const entry = sheet.entry;
  const size = entry.sizes.find(x => x.quantity == s.read.quantity);
  const missing = s.resolved.missing || [];
  return {
    sheet: "open",
    menu_item_id: entry.id,
    product: entry.name,
    size: (size && sizeLabel(size)) || undefined,
    selected: s.resolved.details || "nothing chosen",
    still_missing: missing.length ? missing : undefined,
    nothing_missing: !missing.length,
    ...extra
  };
};

const noSheet = () => ({ error: "No option sheet is open. Use open_option_sheet first." });

// options the waiter named that fit more than one choice (set when the sheet opens)
let lastUnclear = [];
let lastUnclearFor = "";

// The waiter usually says the options in the same sentence as the product ("a Magic Wrap with wedges and a Mirinda for table 18").
// The sheet is often opened by the code or by the model without them, so the model gets the sentence back and applies it first.
const saidNote = filled => {
  const unclear = lastUnclear;
  const said = String(lastHeard || "").trim();
  if (!said) return "";
  return " The waiter's own words were: \"" + said + "\". "
    + (filled ? "What matched was filled in already (see selected). " : "")
    + (unclear && unclear.length ? "The waiter probably named one of these but it is unclear which, ask which: " + unclear.map(u => u.text).join(" | ") + ". " : "")
    + "Everything in these words that is a size, option, extra or ingredient to leave out of this product, and is not in 'selected' yet, must be applied NOW with update_option_sheet before you ask anything; ask only for what is still missing and never ask again for something the waiter already said. "
    + "If the waiter said something that is not among the allowed choices (for example a drink the menu does not have), tell the waiter it is not available and name the real choices; never pick another choice on your own.";
};

const abandonSheet = () => {
  if (sheet) clearTimeout(sheet.timer);
  sheet = null;
  setOrderSheetObserver(null);
};

// the waiter ordered or closed the sheet on the screen (or the model did through the buttons)
const finishSheet = (how, data) => {
  const current = sheet;
  if (!current) return sheetFinish;
  sheet = null;
  clearTimeout(current.timer);
  setOrderSheetObserver(null);
  const run = async () => {
    if (how !== "ordered") {
      if (!current.silent) {
        console.info("voice order sheet closed on screen without ordering:", current.entry.name);
        tellModel("The waiter closed the option sheet of " + current.entry.name + " on the screen WITHOUT ordering it: nothing was ordered or added. Stop asking about its options. Say in one short sentence that it was cancelled and ask what else the waiter wants.");
        requestAnswer();
      }
      return { closed: true };
    }
    const labels = ((data && data.additions) || []).map(a => (a.addition ? "+ " : "- ") + menuItemName(a.menuItemId)).join(", ");
    const text = current.entry.name + (labels ? " (" + labels + ")" : "");
    let failed = false;
    if (current.quick) {
      const previous = orderListUtil.statesShown;
      orderListUtil.statesShown = newOrderStates();
      try {
        await orderAndPrint();
      } catch (e) {
        failed = true;
        console.error("voice order sheet send failed", e);
      } finally {
        orderListUtil.statesShown = previous;
      }
    }
    const result = failed
      ? { ordered: text, error: "The product was ordered but sending or printing failed; the waiter has to finish it on the screen." }
      : { ordered: text, sent: !!current.quick };
    if (!current.silent) {
      console.info("voice order sheet ordered on screen:", text, failed ? "(sending failed)" : "");
      tellModel("The waiter ordered the option sheet on the screen: " + text + (failed ? ". Sending or printing failed, the waiter has to finish it on the screen. Tell the waiter in one short sentence." : ". It is done, do not add it again and stop asking about its options. Say in one short sentence that it was ordered and ask what else the waiter wants."));
      requestAnswer();
    }
    return result;
  };
  sheetFinish = run();
  return sheetFinish;
};

const echoSheet = () => {
  if (!sheet) return;
  const s = sheetState();
  if (!s || s.resolved.error || s.key === sheet.lastKey) return;
  sheet.lastKey = s.key;
  const r = sheetResult(s);
  console.info("voice order sheet changed on screen:", r.product, r.selected, r.still_missing ? "still missing " + r.still_missing.length : "nothing missing");
  tellModel("The waiter changed the option sheet of " + r.product + " on the screen. Now: " + (r.size ? "size " + r.size + ", " : "") + r.selected + ". "
    + (r.still_missing ? "Still missing: " + r.still_missing.join(" | ") : "Nothing is missing.")
    + (r.still_missing
      ? " React as if the waiter had said it: briefly acknowledge the choice and ask for the next missing part, naming the allowed choices. Do not ask again for what is selected."
      : " React as if the waiter had said it: briefly say the product is complete and ask whether to order it. Do not order it yourself before the waiter says so."));
  // a change made on the screen gets an answer like a spoken one
  requestAnswer();
};

const sheetObserver = {
  changed: () => {
    if (!sheet) return;
    clearTimeout(sheet.timer);
    sheet.timer = setTimeout(echoSheet, SHEET_ECHO_MS);
  },
  submitted: data => { finishSheet("ordered", data); },
  closed: () => { finishSheet("closed"); }
};

// The waiter tapped a product and its option dialog opened without the model: take the dialog over, so the model is told
// what it contains and about every later change, exactly as for a sheet it opened itself. The screen orders it (not quick).
const adoptSheet = entry => {
  sheet = { entry, item: null, quick: false, timer: null, lastKey: null, silent: false };
  setOrderSheetObserver(sheetObserver);
  const s = sheetState();
  if (!s || s.resolved.error) {
    abandonSheet();
    return;
  }
  sheet.lastKey = s.key;
  const r = sheetResult(s);
  console.info("voice order tapped sheet adopted:", r.product, r.selected, r.still_missing ? "still missing " + r.still_missing.length : "nothing missing");
  tellModel("The waiter tapped " + r.product + " on the screen and its option sheet is open. Now: " + (r.size ? "size " + r.size + ", " : "") + r.selected + ". "
    + (r.still_missing ? "Still missing: " + r.still_missing.join(" | ") : "Nothing is missing.")
    + (r.still_missing
      ? " Ask the waiter for the missing parts now, one at a time, naming the allowed choices (the waiter can answer by voice or tap on the screen); use update_option_sheet for what is said."
      : " The waiter fills it in on the screen and you are told about every change.")
    + " Do not ask again for what is selected, and do not order it yourself unless the waiter says so.");
  // like when the waiter says the product: the model reacts and asks what is still open
  if (r.still_missing) requestAnswer();
};

const watchTappedSheet = tap => {
  if (sheet || !tap) return;
  const entry = menuIndex().find(e => e.id == tap.id);
  if (!entry) return;
  let tries = 0;
  const look = () => {
    if (sheet || !pc) return;
    if (readOrderSheet()) {
      adoptSheet(entry);
      return;
    }
    if (++tries < 15) setTimeout(look, 150);
  };
  setTimeout(look, 150);
};

const renderSheet = (entry, quantity, additions, updated) => {
  orderMenuItem(sheet && sheet.item ? sheet.item : document.createElement("li"), entry.full.entityType, entry.id, quantity,
    undefined, undefined, undefined, undefined, sheetStrings(additions), undefined, undefined, undefined, undefined, updated, false, true);
};

// The model sometimes opens the sheet empty although the waiter already said the options in the same sentence ("Magic Wrap with
// wedges and a Mirinda in a menu"). What the waiter said is matched against the choices, extras and sizes of the product, so it
// is filled in whatever the model passed. Only unambiguous names are taken; the model's own choices win.
let prefilledFor = "";

// How much of a choice's name the waiter said. Shared by the prefill and by the check of what the model picks.
const spokenMatcher = (entry, spoken) => {
  const productWords = entry.nameTokens.reduce((all, t) => all.concat(t), []);
  // "pipi burgonya" is one word on the menu ("Pipiburgonya"): neighbouring spoken words are also tried joined together
  const forms = spoken.concat(spoken.slice(1).map((w, i) => spoken[i] + w));
  // the words of a name that identify it: numbers and units ("0,33", "dl") are not said the same way and are ignored
  const heard = (s, w) => sameWord(s, w) || (Math.min(s.length, w.length) >= 5 && (s.includes(w) || w.includes(s)));
  // how much of a name was said. A part of the name is enough ("Nagy Pipiburgonya" -> "pipi burgonya") when a word that
  // identifies it (5 letters or more) was said; the choice among several such names is decided by the caller.
  const said = name => {
    const nt = tokens(name).filter(w => !productWords.some(p => sameWord(w, p)) && w.length >= 3 && !/^\d/.test(w));
    const hit = nt.filter(w => forms.some(s => heard(s, w)));
    return { hit: hit.length, all: nt.length, strong: hit.some(w => w.length >= 5) || (hit.length === nt.length && hit.length > 0) };
  };
  const share = s => (s.all ? s.hit / s.all : 0);
  const fits = s => s.hit > 0 && s.strong && share(s) >= 0.5;
  return { said, share, fits };
};

// every option group the product can show: its own and, repeatedly, the ones that choices bring in
const sheetGroups = entry => {
  const groups = [];
  const visit = group => {
    if (groups.includes(group)) return;
    groups.push(group);
    group.choices.forEach(c => followOf(entry, c.id).groups.forEach(visit));
  };
  entry.groups.forEach(visit);
  return groups;
};

// The model picks a choice that the waiter did not say although the waiter clearly named another one of the same group
// (the answer "medium" became the onion rings). Returns the text of the first such mistake, or null. When the words match
// no choice of the group at all ("the first one", a garbled name) the model's pick is believed.
const wrongPick = (entry, picked) => {
  const spoken = tokens(lastHeard);
  if (!spoken.length || !picked.length) return null;
  const { said, share, fits } = spokenMatcher(entry, spoken);
  for (const group of sheetGroups(entry)) {
    if (group.type === "separate") continue;
    const pick = group.choices.find(c => picked.includes(c.id));
    if (!pick) continue;
    const named = group.choices.map(c => ({ c, s: said(c.name) })).filter(h => fits(h.s));
    if (!named.length || named.some(h => h.c.id === pick.id)) continue;
    return group.name + ": the waiter said " + named.map(h => h.c.name).join(" / ") + ", not " + pick.name;
  }
  return null;
};

const spokenOptions = (entry, args) => {
  const spoken = tokens(lastHeard);
  const result = { options: [], size: null, labels: [], unclear: [] };
  if (!spoken.length || prefilledFor === lastHeard) return result;
  const { said, share, fits } = spokenMatcher(entry, spoken);
  const groups = sheetGroups(entry);
  const given = (Array.isArray(args.options) ? args.options : []).map(o => Number(o && typeof o === "object" ? o.menu_item_id : o));
  const candidates = [];
  groups.forEach(group => {
    const hits = group.choices.map(c => ({ id: c.id, name: c.name, s: said(c.name), group })).filter(h => fits(h.s));
    if (!hits.length || group.choices.some(c => given.includes(c.id))) return;
    if (group.type === "separate") {
      hits.forEach(h => candidates.push(h));
      return;
    }
    // only one choice fits: the one with the largest part of its name said wins, a tie is left to the waiter
    const top = Math.max(...hits.map(h => share(h.s)));
    const best = hits.filter(h => share(h.s) === top);
    if (best.length === 1) candidates.push(best[0]);
    // every choice of the group is blocked until the waiter says which one: the model picked an unrelated one (onion rings) on its own
    else result.unclear.push({ text: group.name + ": " + best.map(h => h.name).join(" / "), ids: group.choices.map(c => c.id) });
  });
  entry.optional.forEach(x => {
    if (!given.includes(x.id) && fits(said(x.name))) candidates.push({ id: x.id, name: x.name, group: null });
  });
  // a choice that is only available after another one needs that one first: repeat until nothing more fits
  const kept = [];
  let changed = true;
  while (changed) {
    changed = false;
    candidates.forEach(c => {
      if (kept.includes(c)) return;
      const test = resolveOptions(entry, (args.options || []).concat(kept.concat(c).map(k => ({ menu_item_id: k.id }))), args.without, true);
      if (test.error) return;
      kept.push(c);
      changed = true;
    });
  }
  result.options = kept.map(k => ({ menu_item_id: k.id }));
  result.labels = kept.map(k => k.name);
  if (!kept.length) {
    console.info("voice order spoken options: nothing matched for", entry.name, "heard", JSON.stringify(spoken.join(" ")), "candidates", candidates.length, "| groups:",
      groups.map(g => g.gid + " " + g.name + " [" + g.choices.slice(0, 12).map(c => c.name).join(", ") + "]").join(" ; "), "| extras:", entry.optional.map(x => x.name).join(", "));
  }
  if (!args.size && entry.sizes.length > 1) {
    const sizeHits = entry.sizes.filter(q => sizeLabel(q) && tokens(sizeLabel(q)).every(w => w.length >= 3 && spoken.some(s => sameWord(s, w))));
    if (sizeHits.length === 1) result.size = sizeLabel(sizeHits[0]);
  }
  if (result.options.length || result.size) prefilledFor = lastHeard;
  return result;
};

// "Instead, a Magic Wrap": what is selected in the open sheet is taken over to the new product as far as the new product offers it.
// What the waiter says in the same sentence wins per group. Returns a function that fits it to the new product.
const carryOver = args => {
  const old = sheet;
  const read = old && readOrderSheet();
  if (!read || old.entry.id == args.menu_item_id) return null;
  const inputs = sheetInputs(old.entry, read.adds);
  const oldSize = old.entry.sizes.find(x => x.quantity == read.quantity);
  const oldSizeLabel = oldSize ? sizeLabel(oldSize) : "";
  console.info("voice order sheet settings to carry over from", old.entry.name + ":", JSON.stringify(inputs), oldSizeLabel);
  return (entry, blocked) => {
    const out = { options: [], without: [], size: null, labels: [] };
    const groups = sheetGroups(entry);
    // a group in which the waiter already named something is left alone
    const taken = group => !!group && group.choices.some(c => blocked.includes(c.id));
    // a choice that only exists after another one needs that one first: repeat until nothing more fits
    let changed = true;
    while (changed) {
      changed = false;
      inputs.options.forEach(o => {
        if (out.options.some(x => x.menu_item_id === o.menu_item_id) || blocked.includes(o.menu_item_id)) return;
        const group = groups.find(g => g.choices.some(c => c.id === o.menu_item_id));
        if (taken(group)) return;
        const test = resolveOptions(entry, blocked.map(id => ({ menu_item_id: id })).concat(out.options, o), out.without, true);
        if (test.error) return;
        out.options.push(o);
        out.labels.push(menuItemName(o.menu_item_id));
        changed = true;
      });
    }
    inputs.without.forEach(id => {
      const test = resolveOptions(entry, out.options, out.without.concat(id), true);
      if (!test.error) out.without.push(id);
    });
    if (oldSizeLabel && entry.sizes.length > 1) {
      const match = entry.sizes.find(s => norm(sizeLabel(s)) === norm(oldSizeLabel));
      if (match) out.size = sizeLabel(match);
    }
    return out;
  };
};

// The dialog of the previous product that was ordered or closed is still fading out for a moment, and then empties itself. A dialog that
// is opened in that moment is ignored by the modal ("show" during a transition) or wiped by the emptying: wait until it is really gone.
const dialogHiding = () => {
  const dialog = $("#editMenuItemDialog");
  return !!readOrderSheet() && !dialog.hasClass("show") && !dialog.hasClass("in");
};

const waitDialogGone = async () => {
  const until = Date.now() + 2500;
  while (dialogHiding() && Date.now() < until) await pause(50);
  if (dialogHiding()) console.info("voice order: the previous option dialog did not go away in time");
};

const openSheetTool = async args => {
  let carried = null;
  if (sheet) {
    // the waiter wants another product (or the model misunderstood): the open sheet is replaced, not defended
    if (readOrderSheet()) {
      console.info("voice order: replacing the open option sheet of", sheet.entry.name);
      carried = carryOver(args);
      await closeOpenSheet(true);
      if (readOrderSheet()) return { error: "The option sheet of " + (sheet ? sheet.entry.name : "the previous product") + " could not be closed. Tell the waiter to close it on the screen." };
    }
    abandonSheet();
  }
  const failed = await prepareTable(args);
  if (failed) return failed;
  const info = getTableInfo();
  if (!info.hasTable || info.needsLock) return { error: "No table is open yet. Ask the waiter which table, then call select_table." };
  const entry = menuIndex().find(e => e.id == args.menu_item_id);
  if (!entry) return { error: "unknown menu_item_id " + args.menu_item_id + ", use search_menu first" };
  let prefilled = null;
  lastUnclear = [];
  try {
    prefilled = spokenOptions(entry, args);
    lastUnclear = prefilled.unclear || [];
    lastUnclearFor = lastHeard;
  } catch (e) {
    console.error("voice order: spoken options failed", e);
  }
  if (prefilled && (prefilled.options.length || prefilled.size)) {
    console.info("voice order sheet filled from what the waiter said:", JSON.stringify(lastHeard), "->", prefilled.size || "", prefilled.labels.join(", "));
    args = { ...args, options: (args.options || []).concat(prefilled.options), size: args.size || prefilled.size || undefined };
  }
  if (carried) {
    // the settings of the replaced sheet fill what the waiter did not name again (groups that were unclear stay empty)
    const idOf = o => Number(o && typeof o === "object" ? o.menu_item_id : o);
    const unclearIds = (lastUnclear || []).reduce((all, u) => all.concat(u.ids), []);
    const take = carried(entry, (args.options || []).map(idOf).concat(unclearIds));
    console.info("voice order sheet settings carried over to", entry.name + ":", take.labels.join(", "), take.size || "", "without", take.without.join(","));
    args = {
      ...args,
      options: (args.options || []).concat(take.options),
      without: (args.without || []).concat(take.without.filter(id => !(args.without || []).includes(id))),
      size: args.size || take.size || undefined
    };
  }
  const chosen = sizeFor(entry, args.size);
  if (chosen.error) return chosen;
  const resolved = resolveOptions(entry, args.options, args.without, true);
  if (resolved.error) return resolved;
  await waitDialogGone();
  // the dialog captures the order state when it is created, in quick mode a new order state is needed
  const quick = isQuickOrderTable();
  const previous = orderListUtil.statesShown;
  const item = document.createElement("li");
  const render = () => {
    if (quick) orderListUtil.statesShown = newOrderStates();
    try {
      sheet = { entry, item, quick, timer: null, lastKey: null, silent: false };
      renderSheet(entry, chosen.q.quantity, resolved.additions, false);
    } finally {
      if (quick) orderListUtil.statesShown = previous;
    }
  };
  render();
  // a dialog that was hiding without the code noticing empties itself right after: look again shortly and render once more
  await pause(400);
  if (sheet && !readOrderSheet()) {
    console.info("voice order: the option dialog of", entry.name, "was wiped while opening, opening it again");
    await waitDialogGone();
    render();
  }
  if (!$("#editMenuItemDialog #orderMenuItem").length) {
    sheet = null;
    return { error: "The option sheet could not be opened on the screen." };
  }
  setOrderSheetObserver(sheetObserver);
  const s = sheetState();
  if (!s) return { error: "The option sheet could not be read." };
  sheet.lastKey = s.key;
  return sheetResult(s, { note: "The sheet is open on the screen, the waiter can tap in it." + saidNote(prefilled && (prefilled.options.length || prefilled.size)) });
};

const updateSheetTool = async args => {
  if (!sheet) return noSheet();
  // the waiter did not say which of several similar choices ("pipi fries": medium or large): the model must ask, not pick
  if (lastUnclear.length && lastUnclearFor === lastHeard) {
    const picked = (Array.isArray(args.options) ? args.options : []).map(o => Number(o && typeof o === "object" ? o.menu_item_id : o));
    const open = lastUnclear.filter(u => u.ids.some(id => picked.includes(id)));
    if (open.length) {
      console.info("voice order update_option_sheet refused, the waiter did not say which:", open.map(u => u.text).join(" | "));
      return { error: "The waiter has not said which one. Ask the waiter now, naming the choices, and use update_option_sheet only with the answer: " + open.map(u => u.text).join(" | ") };
    }
  }
  const read = readOrderSheet();
  if (!read) {
    abandonSheet();
    return { error: "The option sheet is not open any more." };
  }
  const entry = sheet.entry;
  const wrong = wrongPick(entry, (Array.isArray(args.options) ? args.options : []).map(o => Number(o && typeof o === "object" ? o.menu_item_id : o)));
  if (wrong) {
    console.info("voice order update_option_sheet refused, not what the waiter said:", wrong, "| heard", JSON.stringify(lastHeard));
    return { error: "That is not what the waiter said (" + wrong + "). Choose again by the name the waiter said, or ask the waiter." };
  }
  let quantity = read.quantity;
  if (args.size) {
    const chosen = sizeFor(entry, args.size);
    if (chosen.error) return chosen;
    quantity = chosen.q.quantity;
  }
  const inputs = changedInputs(entry, sheetInputs(entry, read.adds), args);
  const resolved = resolveOptions(entry, inputs.options, inputs.without, true);
  if (resolved.error) return resolved;
  renderSheet(entry, quantity, resolved.additions, true);
  const s = sheetState();
  if (!s) return { error: "The option sheet could not be read." };
  sheet.lastKey = s.key;
  return sheetResult(s);
};

const confirmSheetTool = async () => {
  if (!sheet) return noSheet();
  const s = sheetState();
  if (!s) {
    abandonSheet();
    return { error: "The option sheet is not open any more." };
  }
  if (s.resolved.error) return { error: s.resolved.error };
  if (s.resolved.missing && s.resolved.missing.length) return { error: "The sheet cannot be ordered yet.", still_missing: s.resolved.missing };
  if (!isOrderingState()) {
    return { status: "waiting_for_waiter", note: "Only the waiter orders here. Tell the waiter to check the sheet and press OK on the screen." };
  }
  const current = sheet;
  current.silent = true;
  $("#editMenuItemDialog button#select").first().trigger("click");
  if (sheet === current) {
    current.silent = false;
    return { error: "The screen did not accept it, a required choice is probably highlighted. Ask the waiter to check the sheet.", ...sheetResult(sheetState() || s) };
  }
  return sheetFinish;
};

// Closes the open option dialog like the close button and waits until it is really gone: the dialog empties itself after its hide
// animation, which would wipe a new sheet that was rendered meanwhile. silent: the model is not told (it asked for it itself).
const closeOpenSheet = async silent => {
  const current = sheet;
  if (!current) return;
  if (silent) current.silent = true;
  $("#editMenuItemDialog button#close").first().trigger("click");
  if (readOrderSheet()) {
    try {
      $("#editMenuItemDialog").modal("hide");
    } catch (e) {
      console.error("voice order: could not hide the option sheet", e);
    }
  }
  const until = Date.now() + 2000;
  while (readOrderSheet() && Date.now() < until) await pause(50);
  if (sheet === current) abandonSheet();
};

const closeSheetTool = async () => {
  if (!sheet) return noSheet();
  await closeOpenSheet(true);
  return { closed: true };
};

// The waiter cancels the open sheet by voice ("mégsem", "hagyjuk", "nem ezt kértem"). The model is not relied on to call close_option_sheet:
// the sheet is closed like with the close button, and the model is told, so it says that it was cancelled. Phrases that are also
// option names ("Nem kérem menüben") or plain answers ("nem") are not used.
const CANCEL_PHRASES = [
  "megsem", "megse", "hagyjuk", "felejtsd el", "felejtsuk el", "felejtsd", "zard be", "zarjuk be", "csukd be", "csukjuk be",
  "nem ezt", "nem ez kellett", "felreertetted", "felreertettel", "felreertes", "rosszul ertetted", "rosszul hallottad", "torold az egeszet",
  "cancel", "never mind", "nevermind", "forget it", "close it", "scrap that"
];

// the utterance for which the model was already told to switch the product of the open sheet
let switchedFor = "";

// A product name alone while a sheet is open is not a request to switch: the speech recognition also hears background talk and the
// room ("Nagy Kakas Burger Menü" came out of nothing and replaced a finished wrap). Only words that mean a change of mind count.
const SWITCH_PHRASES = ["inkabb", "helyett", "legyen", "hanem", "csereljuk", "cserelj", "mas", "instead", "rather", "switch", "change it to", "make it"];
const wantsSwitch = transcript => {
  const said = " " + tokens(transcript).join(" ") + " ";
  return SWITCH_PHRASES.some(p => said.includes(" " + p + " "));
};

const cancelSheetByVoice = transcript => {
  lastHeard = transcript || lastHeard;
  if (!sheet || !readOrderSheet()) return false;
  const said = " " + tokens(transcript).join(" ") + " ";
  const phrase = CANCEL_PHRASES.find(p => said.includes(" " + p + " "));
  if (!phrase) {
    console.info("voice order heard while the option sheet is open:", JSON.stringify(transcript));
    // the waiter names another product ("instead, a Magic Wrap"): the model is told to switch, the open sheet is replaced by its call
    const other = namedProduct(tokens(transcript));
    if (other && other.id !== sheet.entry.id && switchedFor !== transcript && !wantsSwitch(transcript)) {
      console.info("voice order ignored: another product named while the sheet is open without asking to switch:", other.name);
      tellModel("The waiter said \"" + transcript + "\" while the option sheet of " + sheet.entry.name + " is open. That may be background talk or noise. Do NOT open another product or close the sheet because of it; continue with the open sheet, and only if the waiter clearly wants the other product (says instead, rather, cancel) switch.");
    } else if (other && other.id !== sheet.entry.id && switchedFor !== transcript) {
      switchedFor = transcript;
      console.info("voice order waiter named another product while the sheet is open:", sheet.entry.name, "->", other.name);
      tellModel("The waiter wants " + other.name + " (menu_item_id " + other.id + ") instead of " + sheet.entry.name + ". Call open_option_sheet for " + other.name + " NOW: the open sheet is closed and replaced by that call, and the choices already made are taken over where the new product offers them. Do not close the sheet separately and do not ask first.");
      requestAnswer();
    }
    return false;
  }
  console.info("voice order sheet cancelled by voice:", JSON.stringify(transcript), "->", phrase);
  const current = sheet;
  $("#editMenuItemDialog button#close").first().trigger("click");
  if (sheet === current && readOrderSheet()) {
    try {
      $("#editMenuItemDialog").modal("hide");
    } catch (e) {
      console.error("voice order: could not hide the option sheet", e);
    }
  }
  // the close handler tells the model (finishSheet); when it did not run, do it here
  if (sheet === current) {
    abandonSheet();
    tellModel("The waiter cancelled the option sheet of " + current.entry.name + ": nothing was ordered or added. Say in one short sentence that it was cancelled and ask what else the waiter wants.");
    requestAnswer();
  }
  return true;
};

const runSheetTool = (name, args) => {
  const run = async () => {
    try {
      if (name === "open_option_sheet") {
        closeCategoryChoice();
        return await openSheetTool(args);
      }
      if (name === "update_option_sheet") return await updateSheetTool(args);
      if (name === "confirm_option_sheet") return await confirmSheetTool(args);
      return await closeSheetTool(args);
    } catch (e) {
      console.error("voice order option sheet tool failed", name, e);
      return { error: "tool failed" };
    }
  };
  const result = openChain.then(run);
  openChain = result.catch(() => {});
  return result;
};
